"use client";

import { idleMs } from "./activity";

/**
 * Typed client for the gateway's session-gated API. Every call includes the session cookie
 * (`credentials: "include"`). The dashboard NEVER talks to the DB, OMS, or NATS directly —
 * only the gateway (CLAUDE.md: keeps DB pooling sane, one auth/authorization surface).
 */
const GATEWAY_URL =
  process.env.NEXT_PUBLIC_GATEWAY_URL ?? "http://localhost:8090";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    /**
     * The rest of the error body. Several routes return context the user needs — the master
     * holding an account (`conflictingMaster`), whether that came from a legacy user-wide row
     * (`legacy`), how many positions are open (`openPositions`). Dropping it meant the gateway
     * computed a specific, actionable reason and the UI showed a generic sentence instead.
     */
    public details: Record<string, unknown> = {},
  ) {
    super(code);
  }
}

/**
 * A LOST SESSION MUST NOT LOOK LIKE AN EMPTY ACCOUNT.
 *
 * Callers across this app use `.catch(() => [])` / `.catch(() => {})` — 31 of them — so a failed
 * request renders as "no data" rather than as an error. That is reasonable for a genuinely
 * optional panel, but catastrophic for 401: when a session expires or is revoked, EVERY dashboard
 * call fails at once and the user is shown a fully-rendered page reporting zero subscriptions,
 * zero accounts and zero open positions. On a trading product that reads as "my positions are
 * gone", which is a support incident and a panic trigger, not a login prompt.
 *
 * Handled centrally for the same reason the audit IP is bound in middleware rather than passed as
 * an argument: a rule enforced at 31 call sites is a rule with holes, and the holes are invisible.
 *
 * Guarded against a redirect loop — /login and /signup call this too (session probes), and
 * bouncing them to /login would spin.
 */
function handleUnauthenticated(): void {
  if (typeof window === "undefined") return;
  const p = window.location.pathname;
  if (
    p.startsWith("/login") ||
    p.startsWith("/signup") ||
    p.startsWith("/reset")
  )
    return;
  // replace(), not assign(): the expired page must not sit in history behind the login screen.
  window.location.replace(`/login?next=${encodeURIComponent(p)}`);
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  // How long since the user last touched the page — the gateway extends an admin's session only
  // when this is under a minute (lib/activity.ts). Omitted on the server, where nobody is idle.
  const idle = idleMs();
  const res = await fetch(`${GATEWAY_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(idle === null ? {} : { "x-twoke-idle-ms": String(idle) }),
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok) {
    if (res.status === 401) handleUnauthenticated();
    throw new ApiError(res.status, body.error ?? `http_${res.status}`, body);
  }
  return body as T;
}

// ---- shared shapes (mirror the gateway responses) ----
export type Subscription = {
  /** The subscription row — what `moveSubscription` names. */
  id?: string;
  /** When this master's subscription may next be moved, or null when it may be moved now. */
  movableAt?: string | null;
  masterId: string;
  masterName?: string;
  plan: string;
  status: string;
  expiresAt: string;
  /**
   * Which exchange account this subscription entitles (migration 0015), or null for a legacy
   * row that covers every account. The dashboard needs it to say which account is actually
   * copying — the allocator joins `ec.id = s.account_id`, so a second account with no
   * subscription of its own is dormant even though the user "has a subscription".
   */
  accountId?: string | null;
  /** The pairs this master trades (migration 0034) — what you may choose from. */
  masterSymbols?: string[] | null;
  /** Your accepted pairs for this subscription; null/undefined = none chosen, no trade copied. */
  allowedSymbols?: string[] | null;
  /** The master's trading style (scalping, intraday, swing). */
  masterStyle?: string;
  /** The scalping pane this subscription copies (migration 0097): "1", "2", "3" or "5". */
  scalpingPane?: string;
  /** The swing DCA can reach this account now: its switch is on and the account is on Bybit or
   *  cTrader (the gateway's pane checks). */
  dcaAvailable?: boolean;
};
export type Account = {
  accountId: string;
  venue: string;
  isDemo: boolean;
  label: string;
  apiKeyMasked: string;
  /** Set when the VENUE is rejecting this key. Null while it authenticates normally. */
  authFailedAt?: string | null;
  /** The venue's own words, e.g. "10003 API key is invalid". Null while healthy. */
  authError?: string | null;
  /** What the account was connected for: 'spot' accounts belong to Spot (never a copy target). */
  purpose?: "copy" | "spot";
  /** An active Spot subscription: the account is Spot's, never a copy target. */
  spotActive?: boolean;
  /** An active copy subscription: the account follows a master, not offered for Spot. */
  copyActive?: boolean;
};
export type Position = {
  symbol: string;
  direction?: string;
  size?: string;
  entry_price?: string;
  stop_loss?: string | null; // surfaced by the OMS read (Binance via openAlgoOrders)
  take_profit?: string | null;
  protection_status?: string;
  master?: string | null; // originating master (why-this-trade attribution)
  intentId?: string | null;
};
export type MasterActivity = {
  masterId: string;
  master: string;
  /** Filled positions still open. */
  open: number;
  /** Orders placed but not filled yet (resting on the venue). */
  pending: number;
  closed: number;
};
export type RiskSettings = {
  masterId: string | null;
  copyMode: string;
  riskPctPerTrade: string;
  maxConcurrentPositions: number;
  maxDailyLossPct: string;
  maxCopyDeviationPct: string;
  fixedLotQty: string | null;
  killSwitch: boolean;
  mirrorLimitOrders: boolean;
  /** Opt-in compound ladder for this row (migration 0058). READ-ONLY through this type: it is
   *  written only by `setCompound`, and `PUT /me/risk-settings` does not touch the column — so a
   *  client that carries every other field back cannot reset it. */
  compoundTrading?: boolean;
  /** The exchange account this row governs; null = the user-wide row. The allocator reads the
   *  MOST SPECIFIC row, so a caller that omits it edits a row the engine may never consult. */
  accountId?: string | null;
  /** Opt-in on the user-wide save: push these caps onto EVERY account, including ones set
   *  individually in the per-account panel. Without it, a deliberate per-account value survives. */
  applyToAllAccounts?: boolean;
  allowedSymbols?: string[] | null;
  /** The follower's leverage, as a CEILING (migration 0042). `null` = the venue's own maximum.
   *  It can only LOWER what the engine asks for, never raise it — see LEVERAGE_CHOICES. */
  maxLeverage?: number | null;
  updatedAt: string;
};

export type MasterInfo = {
  id: string;
  name: string;
  style: string;
  symbols?: string[] | null;
};
/** An accepted payment stablecoin. `symbol` is presentational; `address` is what gets paid. */
export type PaymentToken = {
  address: `0x${string}`;
  symbol: string;
  /** The zero address is the chain's own coin (POL, ETH), paid through `subscribeNative`. */
  native?: boolean;
};
/** A chain a subscription can be paid on (config/chains.ts), with its accepted tokens. */
export type PaymentChain = {
  chain: "polygon" | "ethereum" | "near";
  /** 0 for NEAR, which has no EVM chain id. */
  chainId: number;
  label: string;
  native: { symbol: string; name: string; decimals: 18 | 24 };
  explorer: string;
  publicRpcUrls: string[];
  tokens: PaymentToken[];
};
export const getPaymentChains = () =>
  req<{ chains: PaymentChain[] }>("/payment/tokens").then((r) => r.chains);
export type SubscribeIntent = {
  /** The chain this intent pays on (the buyer's choice; Polygon by default). */
  chain: "polygon" | "ethereum" | "near";
  /** An EVM address, or on NEAR the contract's account id. */
  contractAddress: string;
  /** An EVM token address, or on NEAR the token's account id ("near" for the coin). */
  token: string;
  /** NEAR only: the coin's quote at the rate the gateway wrote to the contract (lib/near.ts);
   *  null = pay in a stablecoin. */
  nativeNear?: {
    requiredYocto: string;
    maxYocto: string;
    rate: string;
    source: string;
    writtenMs: number;
  } | null;
  /** NEAR only: the message the contract reads (ids as 32 hex chars, no master for Spot). */
  nearMsg?: { user_id: string; account_id: string; master_id?: string; product: number; plan: number };
  /** Present when `token` is native POL: the contract's own quote (wei) at the current oracle
   *  answer, and the cap (+1%) the payment is sent with — the payer never pays above it. */
  native?: {
    requiredWei: string;
    maxWei: string;
    oracleAnswer: string;
    roundId: string;
  } | null;
  priceBaseUnits: string;
  /** A referred buyer's FIRST payment (migration 0093): `contractAddress` is then ReferralPay,
   *  which pays this referrer's commission in the same transaction. USDC/USDT only. */
  referral?: {
    referrerIdHex: `0x${string}`;
    wallet: string;
    bps: number;
    commissionBaseUnits: string;
    /** The quote the platform signed (ReferralPay `Quote` + `signature`); valid until `deadline`. */
    quote: {
      userId: `0x${string}`;
      accountId: `0x${string}`;
      masterId: `0x${string}`;
      referrerId: `0x${string}`;
      wallet: `0x${string}`;
      bps: number;
      product: number;
      plan: number;
      deadline: string;
      signature: `0x${string}`;
    };
  } | null;
  /** Subscription v3: copy or Spot, priced apart; Spot is annual only and names no master. */
  product: "copy" | "spot";
  productEnum: number;
  planEnum: number;
  userIdHex: `0x${string}`;
  /** bytes16 of the exchange account this subscription entitles (migration 0015). */
  accountIdHex: `0x${string}`;
  /** Every stablecoin the platform accepts (USDC/USDT). All are 6-decimal, so one price fits all. */
  acceptedTokens: PaymentToken[];
  masterIdHex: `0x${string}`;
  chainId: number;
};

// ---- follower ----
export const getMasters = () =>
  req<{ masters: MasterInfo[] }>("/me/masters").then((r) => r.masters);
// accountId is REQUIRED: subscriptions are per (account, master), so the caller must say which
// of the user's exchange accounts is being subscribed. The gateway rejects an account that is
// not theirs, and the contract reverts on a zero accountId.
// noRefundAck is the buyer's confirmation of the no-refund term from the checkout dialog; the
// gateway refuses the intent without it and records it per purchase in the audit log.
export const getSubscribeIntent = (
  masterId: string,
  plan: string,
  accountId: string,
  noRefundAck: true,
  token?: string,
  product: "copy" | "spot" = "copy",
  chain: "polygon" | "ethereum" | "near" = "polygon",
) =>
  req<SubscribeIntent>(
    `/me/subscribe-intent?product=${product}&plan=${plan}&accountId=${accountId}&chain=${chain}` +
      (noRefundAck ? "&noRefundAck=1" : "") +
      (product === "copy" ? `&masterId=${masterId}` : "") +
      (token ? `&token=${token}` : ""),
  );
/** Any pair of the exchange, up to `max` (gateway `PAIRS_ANY`); read once per page. */
export type PairsConfig = { any: boolean; max: number };
let pairsConfigP: Promise<PairsConfig> | null = null;
export const getPairsConfig = () =>
  (pairsConfigP ??= req<{ pairs?: PairsConfig }>("/me/subscriptions")
    .then((r) => r.pairs ?? { any: false, max: 0 })
    .catch(() => {
      pairsConfigP = null;
      return { any: false, max: 0 };
    }));
/** A pair found on the subscription's exchange: whether it can be picked, and why not. */
export type PairHit = { symbol: string; name: string | null; series: string | null; pickable: boolean; why: string | null };
export const searchPairs = (masterId: string, accountId: string | null, q: string) =>
  req<{ venue: string; max: number; results: PairHit[] }>(
    `/me/pairs/search?${new URLSearchParams({ masterId, q, ...(accountId ? { accountId } : {}) })}`,
  );
/** The Telegram group: linked or not, allowed or not, and a fresh one-time Start link when allowed. */
export type TelegramState = { enabled: boolean; linked: boolean; member: boolean; allowed: boolean; link: string | null };
export const getTelegram = () => req<TelegramState>("/me/telegram");
/** The Discord server: the invite (open to all), linked or not, allowed or not, and a fresh
 *  one-time code to present with /lye in the server when allowed. */
export type DiscordState = {
  enabled: boolean; invite: string | null; linked: boolean; member: boolean; allowed: boolean;
  /** which private channels open to this user: #master and #spot, opened by either subscription */
  master: boolean; spot: boolean; code: string | null;
};
export const getDiscord = () => req<DiscordState>("/me/discord");
export const getSubscriptions = () =>
  req<{ subscriptions: Subscription[] }>("/me/subscriptions").then(
    (r) => r.subscriptions,
  );
/** The account belongs to Spot: what it is connected for decides (the gateway's rule). An account
 *  moved from Spot to copy is a copy account, its Spot subscription unused until it moves back. */
export const isSpotAccount = (a: Pick<Account, "purpose">) => a.purpose === "spot";
export const getAccounts = () =>
  req<{ accounts: Account[] }>("/me/accounts").then((r) => r.accounts);
export const getPositions = (accountId: string) =>
  req<{ positions: Position[] }>(`/me/accounts/${accountId}/positions`).then(
    (r) => r.positions,
  );
export const getMasterActivity = () =>
  req<{ byMaster: MasterActivity[] }>("/me/master-activity").then(
    (r) => r.byMaster,
  );

// ---- master discovery / track record (follower-safe aggregate) ----
export type MasterStats = {
  activeSubscribers: number;
  intents: {
    total: number;
    approved: number;
    rejected: number;
    expired: number;
    /** Proposals that became a real, filled trade of the master's (the card's percentage). */
    entered?: number;
  };
  /** The master's OWN trades that filled — never follower copies or unfilled orders. */
  positions: { total: number; open: number; closed: number; protected: number };
};
export const getMasterStats = (masterId: string) =>
  req<{ stats: MasterStats }>(`/me/masters/${masterId}/stats`).then(
    (r) => r.stats,
  );

// ---- trade history (closed positions) ----
export type Trade = {
  symbol: string;
  direction: string;
  quantity: string;
  entry_price: string;
  stop_loss: string;
  take_profit: string;
  venue: string;
  opened_at: string;
  closed_at: string | null;
  master: string;
};
export const getTrades = (limit = 100) =>
  req<{ trades: Trade[] }>(`/me/trades?limit=${limit}`).then((r) => r.trades);

// ---- trading journal (owner 2026-09-13) ----
// Per user, New York days, Sunday-to-Saturday weeks. Every amount arrives as an exact decimal STRING
// summed in SQL; `winRate` / `returnPct` are display ratios computed by the gateway.
export type JournalPeriod = {
  kind: "day" | "week" | "month";
  start: string; // YYYY-MM-DD, New York
  finish: string; // inclusive
  trades: number;
  wins: number;
  losses: number;
  /** Closed trades the venue never gave a P&L for — counted, never guessed. */
  unknown: number;
  pnl: string;
  pnlSince: string;
  winRate: number | null;
  returnPct: number | null;
};
export type JournalMonthStats = {
  bestSymbol: string | null;
  bestPnl: string | null;
  worstSymbol: string | null;
  worstPnl: string | null;
  /** Average result in R (P&L ÷ the risk the trade was placed with). */
  avgR: string | null;
};
export type Journal = {
  timezone: string;
  weekStartsOn: "sunday";
  month: string;
  gridStart: string;
  gridEnd: string;
  /** The closed-trade balance `returnPct` rewinds from; null when any account could not be read. */
  balance: { value: number | null; unreadable: string[] };
  stats: JournalMonthStats;
  periods: JournalPeriod[];
};
export type JournalTrade = {
  symbol: string;
  direction: string;
  venue: string;
  quantity: string;
  entry_price: string;
  exit_price: string | null;
  stop_loss: string;
  take_profit: string;
  realized_pnl: string | null;
  pnl_source: string | null;
  r_multiple: string | null;
  opened_at: string;
  exited_at: string;
  master: string;
  account_id: string | null;
  account_label: string | null;
};
/** The GENERAL journal (platform admin): every trade the masters took, counted once and only if
 *  it filled; `master` null = every master. Absent = the caller's own copies. */
export type JournalGeneral = { master: string | null };
const journalQs = (account?: string | null, general?: JournalGeneral | null) =>
  general
    ? `&scope=all${general.master ? `&master=${encodeURIComponent(general.master)}` : ""}`
    : account
      ? `&account=${encodeURIComponent(account)}`
      : "";
export const getJournal = (
  month: string,
  account?: string | null,
  general?: JournalGeneral | null,
) =>
  req<Journal>(
    `/me/journal?month=${encodeURIComponent(month)}${journalQs(account, general)}`,
  );
export const getJournalDay = (
  date: string,
  account?: string | null,
  general?: JournalGeneral | null,
) =>
  req<{ timezone: string; date: string; trades: JournalTrade[] }>(
    `/me/journal/day?date=${encodeURIComponent(date)}${journalQs(account, general)}`,
  ).then((r) => r.trades);
/** The general journal's day per scalping pane (1m/2m/5m): trades on members' accounts only. */
export type JournalPane = { pane: string; trades: number; wins: number; losses: number; sumR: string | null };
export const getJournalPanes = (date: string, general: JournalGeneral) =>
  req<{ timezone: string; date: string; panes: JournalPane[] }>(
    `/me/journal/panes?date=${encodeURIComponent(date)}${journalQs(null, general)}`,
  ).then((r) => r.panes);
/** Whether this user may open the journal: an active subscription, or an admin role. */
export const getJournalAccess = () =>
  req<{ allowed: boolean }>("/me/journal/access");
/** Whether the Spot portfolio is open to this user (an active Spot subscription, or a platform_admin). */
export const getSpotPortfolioAccess = () =>
  req<{ allowed: boolean }>("/me/spot/portfolio-access");

// ---- API key expiry (proxied from OMS, read through the key itself) ----
/** `reported: false` = the venue does not say; `expires_at: null` = it says the key never expires. */
export type KeyExpiry = {
  account_id: string;
  reported: boolean;
  expires_at?: string | null;
};
export const getKeyExpiry = (accountId: string) =>
  req<KeyExpiry>(`/me/accounts/${accountId}/key-expiry`);

// ---- live account balance (proxied from OMS) ----
export type AccountBalance = {
  account_id: string;
  balance: { total_usd: string; available_usd: string };
};
export const getAccountBalance = (accountId: string) =>
  req<AccountBalance>(`/me/accounts/${accountId}/balance`);

// ---- platform_admin revenue analytics ----
/** Money arrives as exact decimal strings ("932.70"), never floats. */
export type Revenue = {
  active_subscriptions: number;
  /** Paid or granted, waiting for the user to attach an account (status pending_account). */
  parked_subscriptions: number;
  total_subscriptions: number;
  total_charged_usd: string;
  charged_30d_usd: string;
  byPlan: { product: "copy" | "spot"; plan: string; active: number; parked: number; charged_usd: string }[];
  byMaster: { master: string; active: number; parked: number; charged_usd: string }[];
  /** Spot subscribers as people: active (paid + granted), grants parked until attached, Spot accounts. */
  spot: { active_users: number; paid_users: number; granted_users: number; parked_users: number;
    accounts: number; users: number };
};
export const getRevenue = () => req<Revenue>("/admin/revenue");
export const getRiskSettings = () =>
  req<{ riskSettings: RiskSettings[] }>("/me/risk-settings").then(
    (r) => r.riskSettings,
  );
export const putRiskSettings = (payload: Partial<RiskSettings>) =>
  req<{ riskSettings: RiskSettings }>("/me/risk-settings", {
    method: "PUT",
    body: JSON.stringify(payload),
  }).then((r) => r.riskSettings);
/** THE COMPOUND LADDER AS THE ENGINE SEES IT (migration 0058) — never as a row.
 *
 *  Both calls return this same shape, straight from the OMS: `applies` is the only field that
 *  answers "will my next copy actually be sized bigger", because `compound: true` on a row the
 *  allocator does not resolve — or on an `exact_mirror` account, which derives no size from a
 *  risk % — changes nothing. `reason: "no_copy_target"` means the engine resolves no copy for
 *  this (account, master) at all.
 */
export type CompoundView = {
  accountId?: string;
  account_id?: string;
  compound: boolean;
  applies: boolean;
  copy_mode?: string;
  step?: number;
  cycle_len?: number;
  multiplier?: string;
  base_risk_pct?: string | null;
  effective_risk_pct?: string | null;
  /** What step 4 would risk AFTER the 5% clamp, and whether the clamp makes the ladder flat.
   *  Computed by the same function that sizes the order, so the warning cannot drift. */
  top_risk_pct?: string | null;
  /** The platform ceiling itself, reported by the engine so the UI never hardcodes a number that
   *  lives in four places. */
  max_risk_pct?: string | null;
  capped?: boolean;
  reason?: string;
};

export const getCompound = (accountId: string, masterId: string) =>
  req<CompoundView>(
    `/me/compound?accountId=${encodeURIComponent(accountId)}&masterId=${encodeURIComponent(masterId)}`,
  );

/** Arm or clear compound for ONE account+master. Step-up (2FA) gated server-side: without an
 *  elevation window this throws `step_up_required`, which the button turns into the code panel. */
export const setCompound = (
  accountId: string,
  masterId: string,
  enabled: boolean,
) =>
  req<CompoundView>("/me/compound", {
    method: "POST",
    body: JSON.stringify({ accountId, masterId, enabled }),
  });

// ---- DCA (docs/MD/strategy/dca.md): spot buys on the account, a budget per pair ----
export type DcaPlan = {
  id: string;
  symbol: string;
  venue: string;
  method: string;
  enabled: boolean;
  /** 6-decimal USDT units, as strings (never a float). */
  budgetUnits: string;
  linesSpentUnits: string;
  underSpentUnits: string;
  /** The optional special buy (−70/−80/−90 % from the bull-run high): its own amount, "0" = off. */
  specialBudgetUnits: string;
  specialSpentUnits: string;
  /** pct_down: {step_pct, count, start_price?} (start_price once the runner has read it);
   *  rsi: {parts}; the Fibonacci methods: {}; vca: its blocks (`VcaParams`, + start_price). */
  methodParams: {
    step_pct?: number; count?: number; start_price?: string; parts?: number;
    /** The Fibonacci lines the price was already under at the save, for one drawing (its High's
     *  week and its Low): they buy nothing, their share went to the lines left (inference
     *  `dca_runner._run_plan`, owner 2026-10-26). */
    passed_at_save?: { high_ts: number; low?: string; lines: string[] };
  } & VcaParams;
  /** 1 (spot), 3 or 5 (spot margin). */
  leverage?: number;
  /** The Portfolio page's group this pair sits in (null = none). */
  portfolioGroup?: string | null;
  /** When the follower last changed the plan (ms since epoch, as text). */
  /** When the plan was created (ms, text): a metal's "no chasing" start when no lines_from. */
  createdMs?: string;
  updatedMs?: string;
  /** Spot's exits (0094): what the plan's sales brought in, and what its buy-backs reserved. */
  rebuyBudgetUnits?: string;
  rebuySpentUnits?: string;
};
/** The methods a follower may choose per pair (gateway PUT /me/dca/plans, migration 0084). */
export const DCA_METHODS = {
  fib_pools: "Fibonacci + RSI",
  fib_only: "Fibonacci",
  rsi: "RSI",
  pct_down: "% down",
  vca: "VCA",
} as const;
/** The methods offered on the Spot page itself; `vca` is set on the Spot › VCA page. */
export const SPOT_PAGE_METHODS = ["fib_pools", "fib_only", "rsi", "pct_down"] as const;
/** The Spot page's methods a pair of `category` may run (the engine's `dca.CATEGORY_METHODS`): every
 *  method; a memecoin starts on RSI (under its 0.786 a memecoin is usually dying, so a Fibonacci
 *  method on one is saved with a risk warning — the user decides); a metal runs every method too,
 *  on its own phase. The first is the default. */
export function spotMethodsFor(category: string): (typeof SPOT_PAGE_METHODS)[number][] {
  if (category === "memecoin") return ["rsi", "pct_down", "fib_pools", "fib_only"];
  return [...SPOT_PAGE_METHODS];
}
/** VCA (Spot › VCA; gateway `vcaParams`, engine `dca.VcaSettings`): a Spot method (`base`) with
 *  the follower's own numbers; a number left out runs at the method's default. */
/** Spot's exits on a VCA pair (gateway `exitsParams`, engine `dca.ExitSettings`), all optional. */
export type VcaExits = {
  /** Sell a share when Fear & Greed holds a level: up to 5 steps, while in profit, keeping a core. */
  tp?: {
    steps: { fg: number; sell_pct: number }[];
    min_profit_pct?: number;
    keep_pct?: number;
    confirm_days?: number;
    rearm_below?: number;
  };
  stop?:
    | { mode: "trailing"; start_pct: number; trail_pct: number; sell_pct: number }
    | { mode: "hard"; loss_pct: number; sell_pct: number };
  /** Buy back with a sale's proceeds once the price closes `gap_pct` under that sale. */
  rebuy?: { gap_pct: number };
};
export type VcaParams = {
  exits?: VcaExits;
  base?: "fib_pools" | "fib_only" | "rsi" | "pct_down";
  drawing?: "day" | "week" | "month";
  /** The bars the method decides on; absent = the pair's own (daily for the 20 largest coins and
   *  companies, weekly for the rest). */
  timeframe?: "4h" | "day" | "week" | "month";
  window?: number;
  bull_lines_pct?: number;
  bear_lines_pct?: number;
  line_shares?: number[];
  under_steps?: number[];
  rsi_bonus_pct?: number;
  rsi_len?: number;
  rsi_bull?: number;
  rsi_bear?: number;
  rsi_mult?: number;
  divergence_mult?: number;
  episode_buys?: number;
  rsi_parts?: number;
  pct_step?: number;
  pct_count?: number;
  /** Where a % down ladder starts (the engine's `dca.PCT_FROM`); absent = last week's low. */
  pct_from?: "week_low" | "day_low";
};
/** The numbers each Spot method runs with today (where the VCA page starts). */
export type VcaDefaults = Required<Omit<VcaParams, "base" | "timeframe" | "pct_from">> & {
  timeframes: string[];
  bases: string[];
  lines: string[];
  /** The bases the engine treats as memecoins now (VCA never runs on them). */
  memecoins: string[];
};
export const getVcaDefaults = () => req<VcaDefaults>("/dca/vca-defaults");
export type DcaMethod = keyof typeof DCA_METHODS;
export const DCA_RSI_PARTS = 10;
/** Show a removed pair on the account's list again (no step-up: it changes no budget). */
export const showDcaPair = (accountId: string, symbol: string) =>
  req<{ ok: boolean }>(
    `/me/dca/plans/show?accountId=${encodeURIComponent(accountId)}&symbol=${encodeURIComponent(symbol)}`,
    { method: "POST" },
  );
/** Why this account cannot buy for Spot now, in the user's words; null when it can (the pages'
 *  `canEdit`, the gateway's rules at the save). Pausing and removing never need any of these. */
export function spotBuyBlock(v: Pick<DcaAccountView, "allowed" | "spotAllowed" | "exempt" | "spotUntil" | "killSwitch">): string | null {
  if (!v.allowed) return "Spot runs on a live (mainnet) account.";
  if (v.killSwitch) return "Your kill switch is on: Spot buys nothing until you turn it off in Settings.";
  if (v.spotAllowed === false) return "This exchange key cannot trade spot: allow spot trading on the key at the exchange.";
  if (!v.exempt && !v.spotUntil) return "No active Spot subscription covers this account.";
  return null;
}
export type DcaAccountView = {
  /** The pairs this account took off its list (migration 0091). */
  hidden?: string[];
  venue: string;
  isDemo: boolean;
  /** Followers are mainnet only; a platform_admin's account may be demo. */
  allowed: boolean;
  /** The key's spot permission read on the venue; null = could not be read. */
  spotAllowed: boolean | null;
  /** The active Spot subscription's end (ISO); null = not subscribed. */
  spotUntil: string | null;
  /** A platform_admin needs no subscription. */
  exempt: boolean;
  /** The account's or its owner's kill switch is armed: Spot buys nothing. */
  killSwitch?: boolean;
  /** The account's spot balances read on its venue; null = could not be read (`balanceError`). */
  balances: { coin: string; free: string; locked: string }[] | null;
  balanceError: string | null;
  plans: DcaPlan[];
};
export type DcaPreview = {
  symbol: string;
  venue: string;
  /** The steps under the Low (`key` as the buy's tag names it, `pct` the step) and the five
   *  lines' weights, as the engine splits the lines' share — a VCA plan's own when asked for. */
  under_steps?: { key: string; pct: number }[];
  line_shares?: number[];
  /** Set when the preview was read with a VCA plan's numbers. */
  vca?: { base: string; drawing: string; window: number; timeframe: string | null } | null;
  category: string | null;
  phase: string;
  checks: Record<string, string | null>;
  price: string | null;
  /** Where a VCA % down ladder would start if saved now (`dca.ladder_anchor`): the low of the last
   *  closed week and of the last closed day, with each bar's open (ms). */
  pct_down_start?: Record<"week_low" | "day_low", { price: string; bar_ts: number } | null>;
  /** RSI(14) on the category's bars (`bar_days`). */
  rsi: number | null;
  /** The % down step the pair's own pullbacks suggest (median depth from a zigzag high to the
   *  low after it, on the category's bars, over all the venue's history), offered to the
   *  follower to apply; null when history shows too few swings. */
  pct_down_suggestion?: { step_pct: number; median_pct: number; pullbacks: number; since_ts: number; bar_days: number } | null;
  /** The bars the methods read for this category: 1 daily, 7 weekly. */
  bar_days?: number;
  /** Where the levels are read: the venue, or "Yahoo AAPL" for a stock token's real share. */
  read_on?: string;
  /** Crypto's halving cycle (docs/MD/strategy/dca.md §1i) — information only: it changes no amount. */
  cycle?: { phase: "markup" | "distribution" | "markdown" | "accumulation"; months_since_halving: number; halving: string } | null;
  /** The engine's risk warnings on this pair (an altcoin losing against BTC…): shown on its row,
   *  they never stop a buy — the user decides. */
  warnings?: string[];
  /** The venue's minimum spot order value (USDT, read live as the OMS reads it); null = unread. */
  min_order_usdt?: number | null;
  /** The smallest buy of each Fibonacci method as a share of the budget, in today's phase. */
  smallest_buy_share?: { fib_pools: number; fib_only: number };
  /** Why there is no Fibonacci drawing, and what the user can change; null when there is one. */
  no_drawing?: string | null;
  drawing: {
    low: string;
    high: string;
    /** The drawing's identity — a buy's tag starts with it. */
    high_ts: number;
    /** The candles it is drawn on (daily first for a top project, then weekly, then monthly). */
    timeframe?: "day" | "week" | "month";
    /** false for a metal (its own rules): a line the price is already under is not bought. */
    passed_lines_buy?: boolean;
    /** A metal's lines its plan can buy (given the plan's start), else null. */
    eligible_lines?: string[] | null;
    lines: Record<string, string>;
  } | null;
};
export type DcaBuy = {
  symbol: string;
  /** `<drawing high ts>|<line>`. */
  tag: string;
  status: string;
  quantity: string;
  price: string;
  /** The leverage it was placed with: a 3x or 5x buy took a third or a fifth of its amount from
   *  the follower, the venue lent the rest. 1 on plain spot and on a sale. */
  leverage?: number;
  at: string;
};
export type DcaPortfolioRow = {
  symbol: string;
  enabled: boolean;
  /** USDT set aside for the symbol (budget + special buy). */
  allocated: number;
  /** The follower's own USDT in what is still held (a sale takes its share): at 3x or 5x the
   *  margin, the venue lending the rest. null on a coin held on the exchange without a Spot plan:
   *  what it cost is unknown to Spot. */
  invested: number | null;
  /** What the venue lent for the coins held (a 3x or 5x buy's other parts); 0 on plain spot. */
  loan: number;
  quantity: number;
  /** What the coins held cost on average, loan included. */
  averagePrice: number | null;
  /** The last public price; null = could not be read (then value and profit are null too). */
  price: number | null;
  /** The coins at that price less what the venue lent for them. */
  value: number | null;
  profit: number | null;
  profitPct: number | null;
  /** The owner's layout: the group the holding sits in (null = ungrouped), and its place across
   *  the whole portfolio (null = not placed yet). */
  group: string | null;
  order: number | null;
  /** Switched off by "pause all" (resuming turns it back on). */
  paused: boolean;
  /** "spot": a pair with a Spot plan; "exchange": a coin held on the exchange without one. */
  source: "spot" | "exchange";
  /** The coin's quantity on the exchange now (free + locked); null = the balance was unreadable. */
  onExchange: number | null;
  /** Spot's exits (0094): what the sales gained or lost, and the sale money waiting to buy back. */
  realized?: number;
  rebuyWaiting?: number;
};
export type DcaPortfolio = {
  rows: DcaPortfolioRow[];
  /** Goals in USDT: the whole portfolio's, and per group name. */
  goals: {
    portfolio: number | null; groups: Record<string, number>;
    /** 0098: reaching the goal sells all of it, once. */
    portfolioSell?: boolean; groupSell?: Record<string, boolean>;
  };
  /** Whether the exchange balance could be read (the coins without a plan come from it). */
  exchangeRead: boolean;
};
/** Save the portfolio layout: every pair in display order with its group (null = ungrouped), and the
 *  goals in USDT (empty = none). */
export const setDcaPortfolioLayout = (
  accountId: string,
  items: { symbol: string; group: string | null }[],
  goals: {
    portfolioGoal: string; groupGoals: Record<string, string>;
    /** 0098: sell all once the goal is reached (switching one on asks for the 2FA code). */
    portfolioGoalSell?: boolean; groupGoalSell?: Record<string, boolean>;
  } = { portfolioGoal: "", groupGoals: {} },
) =>
  req<{ ok: true }>("/me/dca/portfolio/layout", {
    method: "PUT",
    body: JSON.stringify({ accountId, items, ...goals }),
  });
export const getDcaPortfolio = (accountId: string) =>
  req<DcaPortfolio>(`/me/dca/portfolio?accountId=${encodeURIComponent(accountId)}`);
/** SELL ALL OF THE SPOT PORTFOLIO: every pair sells what Spot bought on it (1x), each no lower than
 *  `gapPct` % under its last price; `pause` switches every pair's buying off after. Step-up gated.
 *  `sold`: each pair's quantity, floor price and the OMS's answer. */
export const sellAllSpot = (accountId: string, gapPct: number, pause: boolean) =>
  req<{ ok: true; sold: { symbol: string; quantity: string; floor: string | null; outcome: unknown }[] }>(
    "/me/dca/sell-all",
    { method: "POST", body: JSON.stringify({ accountId, gapPct, pause }) },
  );
/** Pause every plan of the account (`paused: true`), or resume the ones the pause switched off. */
export const setDcaPause = (accountId: string, paused: boolean) =>
  req<{ ok: true; changed: number; warnings?: string[] }>("/me/dca/pause", {
    method: "PUT",
    body: JSON.stringify({ accountId, paused }),
  });
/** The portfolio per day since the first fill, the same holdings as the table (sales taken off,
 *  a leveraged buy's loan off its value): invested and value at that day's close. */
export const getDcaPortfolioHistory = (accountId: string) =>
  req<{ days: { day: string; invested: number; value: number }[] }>(
    `/me/dca/portfolio/history?accountId=${encodeURIComponent(accountId)}`,
  ).then((r) => r.days);
/** Download buys as a CSV file (date, pair, quantity, price, amount, status). Fees are not stored. */
export function downloadBuysCsv(buys: (DcaBuy & { side?: "buy" | "sell" })[], name: string): void {
  const cell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const lines = [
    "date_utc,side,pair,quantity,price,amount_usdt,status,leverage,own_usdt",
    ...buys.map((b) =>
      [
        b.at,
        b.side ?? "buy",
        b.symbol,
        b.quantity,
        b.price,
        (Number(b.quantity) * Number(b.price)).toFixed(6),
        b.status,
        String(b.leverage ?? 1),
        ((Number(b.quantity) * Number(b.price)) / Math.max(1, b.leverage ?? 1)).toFixed(6),
      ].map(cell).join(","),
    ),
  ];
  const url = URL.createObjectURL(new Blob([lines.join("\n") + "\n"], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
/** The categories whose methods run today (the engine's CATEGORY_METHODS ∩ CHOSEN_METHODS): every
 *  one — a memecoin on weekly bars, a metal on its own phase. */
export const POOL_CATEGORIES = new Set(["large_cap", "altcoin", "memecoin", "metal", "stock_index", "single_stock"]);

/** The venue a STOCK token trades on, from each venue's naming (the engine's `dca.stock_token`):
 *  bybit's xStocks end in X (AAPLXUSDT), binance's tokens in B (AAPLBUSDT, SPYBUSDT). null for a
 *  pair that is not a stock token (crypto trades on both). */
/** The category a symbol belongs to by its name alone — what the admin's add tile proposes
 *  (owner 2026-10-11: Tesla and SPY are blue chips, never altcoins). A stock token (bybit's end
 *  in X, binance's in B) is an index when its base is a market ETF, a single stock otherwise;
 *  BTC and ETH are the large caps, the known memecoins and gold have their own groups, anything
 *  else is an altcoin. The gateway refuses a stock token saved in a crypto category. */
const INDEX_ETFS = new Set(["SPY", "QQQ", "IWM", "DIA", "VTI", "VOO"]);
/** The shares known before the venues' stock lists are read — the engine's TOKENISED_SHARES
 *  (dca.py). A coin whose name ends in X or B is a coin unless its base is one of these or the
 *  venues list it as a stock (`rememberStockTokens`). */
const TOKENISED_SHARES = new Set([
  "AAPL", "AMZN", "GOOGL", "GOOG", "META", "MSFT", "NVDA", "TSLA", "COIN", "MSTR", "HOOD",
  "CRCL", "NFLX", "AMD", "AVGO", "PLTR", "INTC", "MU", "ORCL", "CRWD", "LLY", "JPM", "XOM",
  "COST", "TSM", "SNDK", "CBRS", "DRAM", "GLW", "NBIS", "BABA", "BRK", ...INDEX_ETFS,
]);
const MEMECOINS = new Set(["DOGE", "SHIB", "PEPE", "BONK", "WIF", "FLOKI", "TRUMP", "PENGU", "BOME"]);
/** The engine's memecoins (`/dca/vca-defaults` → `dca.memecoins`: its own list and binance's
 *  Meme tag), added to the few known above so every page classifies a coin as the engine does.
 *  Reads only add, as in the engine. */
export function rememberMemecoins(bases: readonly string[]): void {
  for (const b of bases) MEMECOINS.add(b.toUpperCase());
}
/** THE STOCK TOKENS THE VENUES LIST (gateway `/dca/stocks`), as the engine reads them
 *  (dca.add_stock_tokens): pair -> its venue, share and name. Filled once the list is read;
 *  until then the known shares above classify. Reads only add, as in the engine. */
export type StockVenue = "bybit" | "binance" | "mexc";
/** One token can be listed by several venues: an xStock (TSLAXUSDT) by bybit AND MEXC. */
const STOCK_TOKENS = new Map<string, { venues: Set<StockVenue>; share: string; name: string | null }>();
export function rememberStockTokens(list: StockList): void {
  for (const r of list.stocks)
    for (const venue of ["bybit", "binance", "mexc"] as const) {
      const t = r[venue];
      if (!t) continue;
      const known = STOCK_TOKENS.get(t.symbol);
      if (known) known.venues.add(venue);
      else STOCK_TOKENS.set(t.symbol, { venues: new Set([venue]), share: r.share, name: r.name });
    }
}
/** A stock token's share and venues from its name alone: xStocks end in X (bybit and MEXC),
 *  binance's bStocks in B, MEXC's Ondo tokens in ON (TSLAONUSDT -> TSLA). */
function tokenByName(symbol: string): { share: string; venues: StockVenue[] } | null {
  const base = symbol.toUpperCase().replace(/USDT$/, "");
  if (base.endsWith("ON") && base.length > 3) return { share: base.slice(0, -2), venues: ["mexc"] };
  if (base.endsWith("X")) return { share: base.slice(0, -1), venues: ["bybit", "mexc"] };
  if (base.endsWith("B")) return { share: base.slice(0, -1), venues: ["binance"] };
  return null;
}
/** The company behind a stock token the venues listed, else null. */
export function stockTokenName(symbol: string): string | null {
  return STOCK_TOKENS.get(symbol.toUpperCase())?.name ?? null;
}
export function categoryOf(symbol: string): string {
  const base = symbol.toUpperCase().replace(/USDT$/, "");
  const read = STOCK_TOKENS.get(symbol.toUpperCase());
  const byName = tokenByName(symbol);
  const share = read ? read.share : byName && TOKENISED_SHARES.has(byName.share) ? byName.share : "";
  if (share && INDEX_ETFS.has(share)) return "stock_index";
  if (share) return "single_stock";
  if (base === "BTC" || base === "ETH") return "large_cap";
  if (MEMECOINS.has(base)) return "memecoin";
  if (base === "PAXG" || base === "XAUT") return "metal";
  return "altcoin";
}

/** The venues that list a stock token (null: not a stock token). */
export function stockTokenVenues(symbol: string, category: string | null | undefined): StockVenue[] | null {
  if (category !== "stock_index" && category !== "single_stock") return null;
  const read = STOCK_TOKENS.get(symbol.toUpperCase());
  if (read) return [...read.venues];
  return tokenByName(symbol)?.venues ?? null;
}

export const getDcaBuys = (accountId: string) =>
  req<{ buys: DcaBuy[] }>(`/me/dca/buys?accountId=${encodeURIComponent(accountId)}`).then(
    (r) => r.buys,
  );
/** The account's Spot buys and sales (0094: take profit, trailing or hard stop), newest first. */
export const getDcaActivity = (accountId: string) =>
  req<{ buys: DcaBuy[]; sales?: DcaBuy[] }>(`/me/dca/buys?accountId=${encodeURIComponent(accountId)}`).then(
    (r) => ({ buys: r.buys, sales: r.sales ?? [] }),
  );
export const getDca = (accountId: string) =>
  req<DcaAccountView>(`/me/dca?accountId=${encodeURIComponent(accountId)}`);
/** Save one pair's plan. Step-up (2FA) gated: throws `step_up_required` without a fresh code. */
export const saveDcaPlan = (args: {
  accountId: string;
  symbol: string;
  budgetUsdt: string;
  specialUsdt?: string;
  method: DcaMethod;
  /** pct_down only. */
  stepPct?: number;
  count?: number;
  /** vca only. */
  vca?: VcaParams;
  /** Keep the plan's pause mark (the VCA page applying numbers, the switch unchanged). */
  keepPaused?: boolean;
  enabled: boolean;
  /** 1 (spot), 3 or 5 (spot margin; the signed agreement is required for 3 and 5). */
  leverage?: number;
  /** Any method but VCA (which carries them in `vca`): its sales; omitted = keep the plan's own,
   *  null = none. */
  exits?: VcaExits | null;
  /** `warnings`: the risk warnings of the save (a category over its suggested share) — shown to
   *  the user, who decides; they never refuse the save. */
}) => req<{ ok: boolean; plan: DcaPlan; warnings?: string[] }>("/me/dca/plans", { method: "PUT", body: JSON.stringify(args) });
/** Take any pair off the account's list (migration 0091): a plan that never placed an order is
 *  deleted, one that bought is switched off and hidden (it stays in the portfolio), an offered
 *  pair is hidden. Step-up gated like a save. */
export const deleteDcaPlan = (accountId: string, symbol: string) =>
  req<{ ok: boolean; outcome: "deleted" | "stopped" | "hidden" }>(
    `/me/dca/plans?accountId=${encodeURIComponent(accountId)}&symbol=${encodeURIComponent(symbol)}`,
    { method: "DELETE" },
  );
/** The leveraged-DCA agreement: its text, and whether this user signed the current version. */
export type DcaLeverageAgreement = { currentVersion: string; acceptedVersion: string | null; accepted: boolean; text: string };
export const getDcaLeverageAgreement = () => req<DcaLeverageAgreement>("/me/dca/leverage-agreement");
/** The take-profit and stop agreement (0094), signed before Spot may sell for the follower. */
export const getDcaExitsAgreement = () => req<DcaLeverageAgreement>("/me/dca/exits-agreement");
export const acceptDcaExitsAgreement = () =>
  req<{ accepted: boolean; version: string }>("/me/dca/exits-agreement/accept", { method: "POST" });
export const acceptDcaLeverageAgreement = () =>
  req<{ accepted: boolean; version: string }>("/me/dca/leverage-agreement/accept", { method: "POST" });
/** The distance of the venue's forced sale from the average entry on spot margin, by leverage
 *  (the agreement's own numbers: 3x about a third, 5x about a fifth). */
export const LEVERAGE_LIQUIDATION_PCT: Record<number, number> = { 1: 0, 3: 33, 5: 20 };
/** The pairs Spot offers, symbol -> category (platform_settings.dca_pairs). */
export const getDcaPairs = () =>
  req<{ pairs: Record<string, string> }>("/dca/pairs").then((r) => r.pairs);
/** Replace the pairs Spot offers. platform_admin + step-up (throws `step_up_required`). */
export const setDcaPairs = (pairs: Record<string, string>) =>
  req<{ ok: boolean; pairs: Record<string, string> }>("/admin/dca/pairs", {
    method: "PUT",
    body: JSON.stringify({ pairs }),
  });
/** The engine's reading of a pair now; with `vca`, the drawing and levels a VCA plan with those
 *  numbers would buy on (the gateway checks them as a save does). */
export const getDcaPreview = (symbol: string, venue: string, vca?: VcaParams, since?: number) =>
  req<DcaPreview>(
    `/dca/preview?symbol=${encodeURIComponent(symbol)}&venue=${encodeURIComponent(venue)}` +
      (vca ? `&vca=${encodeURIComponent(JSON.stringify(vca))}` : "") +
      (since ? `&since=${since}` : ""),
  );

/** The Spot page's market panel (inference `dca_runner.market`) — information only, it changes no
 *  buy. A part that could not be read is null. Numbers from the venues are decimal strings. */
export type DcaMarket = {
  /** The crypto Fear & Greed index (alternative.me): 0 extreme fear … 100 extreme greed. */
  /** Crypto's Fear & Greed: CoinMarketCap's, alternative.me's when that cannot be read. */
  fear_greed: { value: number; label: string; yesterday: number | null; source?: string } | null;
  /** The stocks' Fear & Greed: CNN's index. */
  stock_fear_greed?: { value: number; label: string; yesterday: number | null; source: string } | null;
  crypto: {
    phase: string;
    checks: Record<string, string | null>;
    price: string | null;
    rsi: number | null;
    cycle: DcaPreview["cycle"];
    /** The calendar's next flip (dca.next_phase_switch). */
    next?: { phase: string; date: string };
  } | null;
  /** The share of Spot's altcoin pairs beating BTC over 90 days (the Altcoin Season Index idea). */
  /** `alt_strength`: alts at the alt-season mark outside the bull run after the halving — not an
   *  alt season (owner 2026-10-12). `bull_run`: the calendar is in markup or distribution. */
  alt_season: { share: number | null; beating: string[]; state: "alt_season" | "alt_strength" | "btc_season" | "neutral" | null; btc_90d: number | null; pairs: number; bull_run?: boolean; source?: string } | null;
  /** The Nasdaq 100, read as the S&P 500 is: ^NDX, its phase on QQQ's weekly closes, its 9-week MA. */
  nasdaq?: { phase: string; checks: Record<string, string | null>; ndx: string | null; ndx_ma: IndexMa | null } | null;
  stocks: {
    phase: string;
    checks: Record<string, string | null>;
    vix: string | null;
    spx: string | null;
    /** The S&P 500 against its 9-week average (closed weekly bars). */
    spx_ma?: IndexMa | null;
    /** IWM against SPY: a small-cap run when the ratio is above its 20-week average and IWM above its 40-week. */
    small_caps?: { state: "run" | "lagging" | null; ratio_vs_avg: number | null; above_40w: boolean | null };
    /** The equities' alt season: the share of the 11 sectors beating SPY over 90 days (75/25 bands),
     *  and the equal-weight S&P against the cap-weighted. */
    breadth?: { share: number | null; beating: string[]; state: "broad" | "narrow" | "mixed" | null; equal_weight_vs_avg: number | null; spy_90d: number };
  } | null;
};
/** An index's newest closed weekly close against its short weekly average (dca.weekly_ma). */
export type IndexMa = { weeks: number; ma: string; close: string; above: boolean };
export const getDcaMarket = () => req<DcaMarket>("/dca/market");
/** Whether binance and bybit list a spot pair and trade it now (platform_admin, before adding a
 *  pair). null = that venue could not be read. */
export const getDcaListed = (symbol: string) =>
  req<{ symbol: string; binance: boolean | null; bybit: boolean | null; mexc: boolean | null }>(
    `/admin/dca/listed?symbol=${encodeURIComponent(symbol)}`,
  );

/** A pair's short description, for the hover card on the Spot pages (gateway `/me/asset-profile`:
 *  Yahoo's company profile for a share or fund, CoinGecko's for a coin; kept 30 days). */
export type AssetProfile = {
  symbol: string;
  kind: "crypto" | "stock" | "etf";
  name: string;
  ticker: string;
  rank?: number | null;
  tags: string[];
  /** "" when the source has none; the card says so. */
  description: string;
  url?: string | null;
  source: string;
};
/** `symbol` is a pair (SOLUSDT, AAPLXUSDT) or a share's ticker (AAPL). 404 = nothing found. */
export const getAssetProfile = (symbol: string) =>
  req<AssetProfile>(`/me/asset-profile?symbol=${encodeURIComponent(symbol)}`);

/** A coin in the project finder (inference `scout`): the market's numbers (30-day, 1-year, from
 *  the all-time high), the quality numbers (DefiLlama 30-day revenue and its yield on the market
 *  cap, volume on the market cap) and the venues listing its USDT spot pair. */
export type ScoutCoin = {
  symbol: string; name: string | null; id: string | null; rank: number | null; mcap_b: number;
  vol_mcap: number | null; d30: number; y1: number | null; ath_pct: number;
  rev30_m: number | null; rev_yield_pct: number | null; listed: ("binance" | "bybit" | "mexc")[]; image: string | null;
};
/** A share in the finder (inference `_scout_stocks`): every share bybit or binance tokenises, with
 *  Yahoo's moves and company numbers; a number Yahoo does not give is null. */
export type ScoutStock = {
  symbol: string; name?: string | null; d30: number; d90: number; y1: number | null; vs_spy_90: number; off_high_pct: number;
  kind?: "stock" | "etf";
  mcap_b?: number | null;
  /** Price over the last 12 months' earnings, and over the next 12 months' expected earnings. */
  pe?: number | null;
  fwd_pe?: number | null;
  /** Expected earnings per share over the next 12 months (positive = a profit is expected). */
  eps_fwd?: number | null;
  /** The analysts' average: 1 strong buy … 5 sell, with Yahoo's word for it. */
  analyst?: { score: number; label: string } | null;
  /** The next earnings report (date, UTC), when announced. */
  earnings?: string | null;
  div_yield?: number | null;
  vs_200d_pct?: number | null;
  /** The share's spot token per venue that lists it (bybit `…XUSDT`, binance `…BUSDT`); absent = none. */
  pairs?: Partial<Record<"bybit" | "binance" | "mexc", string>>;
};
export type Scout = {
  /** True while the inference reads the sources in the background: ask again in a few seconds. */
  reading?: boolean;
  read_at: string | null;
  runners: ScoutCoin[];
  revenue: ScoutCoin[];
  blue_chips: ScoutCoin[];
  memecoins: ScoutCoin[];
  narratives: ScoutNarrative[];
  /** The Magnificent 7: the group's ETF moves and each member's own (dca_runner.scout). */
  mag7?: {
    etf: string;
    name: string | null;
    d1: number;
    d30: number;
    d90: number;
    members: { symbol: string; name: string | null; d1: number; d30: number }[];
  } | null;
  /** AI in stocks: the AI ETF's moves and its largest holdings today (dca_runner.scout). */
  stock_ai?: {
    etf: string;
    name: string | null;
    d1: number;
    d30: number;
    d90: number;
    holdings: { symbol: string; name: string; pct: number }[];
  } | null;
  /** The narrative card's readings (dca_runner.scout): best and most popular over every category
   *  over $1b, the AI category, the top perpetuals coin, the blue chip running now and the last
   *  bull run's top runner. Any may be missing when its source was unreadable. */
  spotlight?: {
    best?: ScoutNarrative;
    popular?: ScoutNarrative;
    ai?: ScoutNarrative | null;
    perp?: ScoutCoin | null;
    blue_chip_runner?: ScoutCoin | null;
    bull_run_runner?: { symbol: string; name: string; gain_pct: number; from: string; to: string } | null;
  };
  stocks: ScoutStock[];
  /** The quantum computing theme (dca_runner QUANTUM_SHARES): the ETF's moves and the pure plays,
   *  each with its tokens where a venue lists one. */
  quantum?: { etf: string; name: string | null; d1: number; d30: number; d90: number; shares: ScoutStock[] } | null;
  errors: string[];
};
/** `top` are CoinGecko ids ("tether-gold"); `top_names` the same coins by name ("Tether Gold"). */
export type ScoutNarrative = { name: string; mcap_b: number; d24: number; top: string[]; top_names?: string[] };
export const getDcaScout = () => req<Scout>("/me/dca/scout");

/** One breakout on the breakout list: the bar that closed through the rail on its chart. */
export type DcaBoardBreakout = {
  pane: "3D" | "1W" | "1M";
  bar_ts: number;
  close: string;
  rail: string;
  a_plus: boolean;
  confirmation: string | null;
  confirmation_ts: number | null;
};
export type DcaBoardPair = {
  symbol: string;
  category: string;
  /** Where the weekly MACD stands now. */
  macd: {
    above_signal: boolean | null;
    above_zero: boolean | null;
  };
  vs_ma200_pct: number | null;
  /** The latest breakout on each chart (3-day, weekly, monthly) that gave one. */
  breakouts: DcaBoardBreakout[];
};
export type DcaBoard = { reading: boolean; pairs: DcaBoardPair[]; errors: string[]; as_of?: number };
export const getDcaBoard = () => req<DcaBoard>("/dca/board");
/** One spot pair's public 24-hour statistics on its venue; null = the venue does not list it. */
export type DcaTicker = { price: string; changePct: string; high: string; low: string; quoteVolume: string } | null;
export const getDcaTickers = (venue: string, symbols: string[]) =>
  req<{ tickers: Record<string, DcaTicker> }>(
    `/dca/tickers?venue=${encodeURIComponent(venue)}&symbols=${encodeURIComponent(symbols.join(","))}`,
  ).then((r) => r.tickers);

/** One venue's token for a share (gateway `/dca/stocks`): its pair, whether the venue trades it
 *  now, bybit's multiplier (token price ÷ multiplier = the share's price), its 24-hour ticker
 *  (null = unread) and whether Spot offers that pair. */
export type StockVenueToken = {
  symbol: string;
  status: "trading" | "halted";
  multiplier?: string;
  ticker: DcaTicker;
  offered: boolean;
};
export type StockRow = {
  share: string;
  name: string | null;
  bybit: StockVenueToken | null;
  binance: StockVenueToken | null;
  mexc: StockVenueToken | null;
};
export type StockList = { stocks: StockRow[]; unread: string[]; pricesUnread: string[] };
export const getDcaStocks = () => req<StockList>("/dca/stocks");

// ---- Daily limits: profit cap, consistency rule, loss floor (migrations 0065/0066) ----
/** One trading day's limits for an account, in account currency, AS THE ENGINE RESOLVES THEM. */
export type DailyLimitsDay = {
  /** When this day began (ISO): 00:00 UTC, the venues' day, for every contract. */
  day_start: string;
  /** USDT balance when the day began. null = no limit set (not read). */
  start_balance?: string | null;
  /** What every % is taken of: initial capital, or the start balance when it is not measured. */
  base?: string;
  /** The day's highest result so far (trailing style only). */
  day_peak_result?: string | null;
  /** Where the daily loss is measured from: 0 = start of day, above 0 = the day's peak. */
  daily_reference?: string;
  /** Closed-trade P&L, fees and funding booked since `day_start`. */
  trading_change?: string | null;
  /** Floating P&L of the positions open now. */
  unrealised?: string | null;
  /** Deposits, withdrawals and transfers since `day_start` — NOT part of the day's result. */
  transfers?: string | null;
  /** The day's result: `trading_change + unrealised`, read from the exchange. */
  day_pnl: string;
  loss_floor: string | null;
  profit_floor: string | null;
  consistency_cap: string | null;
  /** The larger of the floor and the consistency cap; null = no profit cap. */
  profit_cap: string | null;
  /** Profit booked since the account first subscribed, up to the start of this day. */
  period_profit: string;
  /** Closed trades in that period whose P&L is unknown — counted as zero. */
  period_unknown_rows: number;
  verdict:
    | "open"
    | "loss_floor_breached"
    | "profit_cap_reached"
    | "overall_drawdown_breached";
};
/** The overall floor since initial capital (migration 0067). */
export type DailyLimitsOverall = {
  /** Balance when the account first subscribed (or restarted after a breach). */
  initial_capital: string | null;
  baseline_at: string | null;
  /** Trading result since then, open trades included, deposits/withdrawals excluded. */
  total_result: string | null;
  /** Negative; null = off or initial capital unknown. */
  total_floor: string | null;
  /** First seen below the floor — new copies stop from here. */
  hit_since: string | null;
  /** Confirmed: copies closed, stays until the follower restarts. */
  breached_at: string | null;
};
export type DailyDrawdownMode = "start_of_day" | "trailing_peak";
export type DailyLimitsView = {
  account_id: string;
  master_id: string;
  equity?: string;
  max_daily_loss_pct?: string | null;
  max_daily_profit_pct?: string | null;
  consistency_share?: string | null;
  daily_drawdown_mode?: DailyDrawdownMode;
  max_total_drawdown_pct?: string | null;
  subscription_started_at?: string | null;
  day?: DailyLimitsDay;
  overall?: DailyLimitsOverall;
  /** "no_copy_target" when the engine resolves no copy for this account+master. */
  reason?: string;
};

export const getDailyLimits = (accountId: string, masterId: string) =>
  req<DailyLimitsView>(
    `/me/daily-limits?accountId=${encodeURIComponent(accountId)}&masterId=${encodeURIComponent(masterId)}`,
  );

/** Set the account limits for ONE account+master: profit cap, consistency share, the daily
 *  style and the overall floor (null = off). ALL FOUR are always sent: the gateway refuses a body
 *  that omits one, so a client can never clear a setting by not knowing about it. Returns the
 *  engine's view after the write. */
export const setDailyLimits = (
  accountId: string,
  masterId: string,
  limits: {
    maxDailyProfitPct: string | null;
    consistencyShare: string | null;
    dailyDrawdownMode: DailyDrawdownMode;
    maxTotalDrawdownPct: string | null;
  },
) =>
  req<DailyLimitsView>("/me/daily-limits", {
    method: "POST",
    body: JSON.stringify({ accountId, masterId, ...limits }),
  });

/** Restart after an OVERALL drawdown breach: initial capital is re-measured from now. Step-up
 *  gated — throws `step_up_required` without an elevation window. */
export const restartDrawdown = (accountId: string, masterId: string) =>
  req<DailyLimitsView>("/me/drawdown/restart", {
    method: "POST",
    body: JSON.stringify({ accountId, masterId }),
  });

// ---- Step-up 2FA (elevation window for sensitive actions) ----
/** Email a fresh step-up code. */
export const sendStepUp = () =>
  req<{ sent: boolean }>("/me/step-up/send", { method: "POST" });
/** Verify the code → grants a ~10-min elevation window for connect-key + admin writes. */
export const verifyStepUp = (code: string) =>
  req<{ elevated: boolean }>("/me/step-up/verify", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
/** Is the user currently elevated? (gate sensitive buttons in the UI). */
export const getStepUpStatus = () =>
  req<{ elevated: boolean }>("/me/step-up/status").then((r) => r.elevated);

/** A cTrader trading account the follower's grant covers — read by the OMS on its own host. */
export type CtraderAccount = {
  ctidTraderAccountId: string;
  isLive: boolean;
  traderLogin: number | null;
  broker: string | null;
  /** The deposit currency; null = the venue could not be read, which the gateway refuses. */
  currency: string | null;
  balance: string | null;
  limitedRisk: boolean | null;
};
/** Step 1 of the cTrader connect: the grant URL at cTrader ID (step-up gated). With `masterId`
 *  the grant is for that master's own trading account (manual master). */
/** What `venue` trades whose NAME or ticker matches `q` (the annotator's search box): "platinum" ->
 *  XPTUSDT. Each with its name and the series it reads (a TradFi market's Yahoo series). Throws when
 *  nothing could be read — never "nothing found" for a failed read. */
export const searchSymbols = (venue: string, q: string, master?: string) =>
  req<{ venue: string; q: string; results: { symbol: string; name: string | null; series: string | null }[] }>(
    `/me/symbol-search?${new URLSearchParams({ venue, q, ...(master ? { master } : {}) })}`,
  );

/** The public addresses the platform's exchange calls come from (to allow on an IP-restricted key). */
export const getEgressIps = () => req<{ ips: string[] }>("/me/egress-ips");

export const startCtrader = (masterId?: string) =>
  req<{ url: string }>("/me/ctrader/start", {
    method: "POST",
    body: JSON.stringify(masterId ? { masterId } : {}),
  });
/** Step 2: hand back what cTrader returned; the OMS exchanges the code. */
export const finishCtrader = (code: string, state: string) =>
  req<{
    accounts: CtraderAccount[];
    masterId: string | null;
  }>("/me/ctrader/callback", {
    method: "POST",
    body: JSON.stringify({ code, state }),
  });
/** Step 3 for a manual MASTER: the picked account becomes the master's own (copying off). */
/** Disconnect a manual master's account — refused while its copying is ON. */
export const disconnectMasterAccount = (masterId: string, venue: string, isDemo: boolean) =>
  req<{ disconnected: boolean }>(
    `/master/${masterId}/accounts/${venue}/${isDemo ? "demo" : "mainnet"}`,
    { method: "DELETE" },
  );
export const connectCtraderMaster = (
  masterId: string,
  ctidTraderAccountId: string,
  mainnetAck?: boolean,
) =>
  req<{ accountId: string }>(`/master/${masterId}/ctrader/connect`, {
    method: "POST",
    body: JSON.stringify({ ctidTraderAccountId, mainnetAck }),
  });
/** Step 3: connect ONE of the granted accounts. */
export const connectCtrader = (ctidTraderAccountId: string) =>
  req<{ accountId: string; label: string; isDemo: boolean }>(
    "/me/ctrader/connect",
    { method: "POST", body: JSON.stringify({ ctidTraderAccountId }) },
  );
export const addCredential = (payload: {
  apiKey: string;
  apiSecret: string;
  venue: string;
  isDemo: boolean;
  label?: string;
  /** 'spot' when connected for Spot (the connect form's Environment choice). */
  purpose?: "copy" | "spot";
}) =>
  req<{ accountId: string }>("/exchange-credentials", {
    method: "POST",
    body: JSON.stringify(payload),
  });
/**
 * Replace the key/secret on an EXISTING account, keeping its id — so the paid subscription
 * (bound to one accountId since migration 0015), the per-account risk settings and any open
 * positions stay attached. Venue and demo/mainnet are intentionally not changeable here; that
 * would be a different account, i.e. `addCredential`.
 */
/**
 * Disconnect an exchange account. Requires a step-up window, the account to be FLAT, and no active
 * subscription — all enforced server-side; this only surfaces the reason.
 */
export const deleteCredential = (accountId: string) =>
  req<{ ok: boolean }>(`/exchange-credentials/${accountId}`, {
    method: "DELETE",
  });

export const rotateCredential = (
  accountId: string,
  payload: {
    apiKey: string;
    apiSecret: string;
    label?: string;
    isDemo?: boolean;
    /** The environment move: Spot ⇄ copy. */
    purpose?: "copy" | "spot";
  },
) =>
  req<{ accountId: string; rotated: boolean }>(
    `/exchange-credentials/${accountId}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
  );
/**
 * Current plan prices in 6-decimal base units, keyed by plan id — the live, admin-settable
 * values from `plan_prices`, never the config/plans.ts seed.
 *
 * The gateway wraps them as `{ prices: {...} }`, so this UNWRAPS before returning. It previously
 * declared `req<Record<string, string>>` and handed the envelope straight to callers, so every
 * `prices[planId]` lookup was undefined and the UI silently fell back to its em-dash placeholder:
 * the landing page showed no prices, and SubscribeModal showed none for the plan being purchased.
 * TypeScript could not catch it because `req<T>` blind-casts the body to T.
 */
/** The current prices of one product's plans (Subscription v3: copy and Spot priced apart). */
export const getPlanPrices = async (product: "copy" | "spot" = "copy"): Promise<Record<string, string>> => {
  const r = await req<{ prices: Record<string, string>; spot: Record<string, string> }>("/plans/prices");
  return (product === "spot" ? r.spot : r.prices) ?? {};
};
/** Whether the masters are closed to new payments (the Slots being built), with the message to show. */
export type MastersPaused = { paused: boolean; ht: string; en: string; short: string };
export const getMastersPaused = async (): Promise<MastersPaused> => {
  const r = await req<{ mastersPaused?: boolean; mastersPausedMessage?: { ht: string; en: string; short: string } }>("/plans/prices");
  return { paused: r.mastersPaused === true, ht: r.mastersPausedMessage?.ht ?? "", en: r.mastersPausedMessage?.en ?? "", short: r.mastersPausedMessage?.short ?? "" };
};

// ---- Manual (human-traded) master accounts (master_admin) ----
export type MasterAccount = {
  venue: string;
  isDemo: boolean;
  label: string;
  copyEnabled: boolean;
  updatedAt: string;
};
export const getMasterAccounts = (masterId: string) =>
  req<{ accounts: MasterAccount[] }>(`/master/${masterId}/accounts`).then(
    (r) => r.accounts,
  );
export const connectMasterKey = (
  masterId: string,
  payload: {
    apiKey: string;
    apiSecret: string;
    venue: string;
    isDemo: boolean;
    label?: string;
    mainnetAck?: boolean;
  },
) =>
  req<{ accountId: string }>(`/master/${masterId}/exchange-credentials`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
export const setMasterCopyEnabled = (
  masterId: string,
  payload: {
    enabled: boolean;
    venue: string;
    isDemo: boolean;
    mainnetAck?: boolean;
  },
) =>
  req<{ enabled: boolean }>(`/master/${masterId}/copy-enabled`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });

// ---- Binance limit-mirroring: declare intended SL/TP for a resting limit ----
// A Binance resting limit can't carry SL/TP (venue rejects them while flat), so the master
// declares them here — the OMS then rests a %-sized bare limit on each Binance follower and
// arms the declared SL/TP on fill. (Bybit needs none: SL/TP ride on the order.)
export type MasterRestingLimit = {
  order_id: string;
  symbol: string;
  side: string; // BUY | SELL
  price: string;
  qty: string;
};
export const getMasterBinanceLimits = (masterId: string) =>
  req<{ limits: MasterRestingLimit[] }>(
    `/master/${masterId}/binance-limits`,
  ).then((r) => r.limits);
export const declareLimitPlan = (
  masterId: string,
  payload: {
    masterOrderId: string;
    symbol: string;
    side: string;
    limitPrice: number;
    stopLoss: number;
    takeProfit: number;
  },
) =>
  req<{ masterOrderId: string }>(`/master/${masterId}/limit-plan`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

// ---- Terms of Service (must be accepted before subscribe / credential-add) ----
export type TosStatus = {
  currentVersion: string;
  acceptedVersion: string | null;
  accepted: boolean;
};
export const getTos = () => req<TosStatus>("/me/tos");
export const acceptTos = () =>
  req<{ accepted: boolean; version: string }>("/me/tos/accept", {
    method: "POST",
  });

// ---- Trading-risk disclosure (shown at /signup, recorded right after the account exists) ----
// Same shape as TosStatus but a SEPARATE document and version — see config/tos.ts.
export const getRiskDisclosure = () => req<TosStatus>("/me/risk-disclosure");
export const acceptRiskDisclosure = () =>
  req<{ accepted: boolean; version: string }>("/me/risk-disclosure/accept", {
    method: "POST",
  });

// ---- GDPR (data export + anonymize-and-retain deletion) ----
/** Fetch the full data export and trigger a browser download. */
export async function downloadMyData(): Promise<void> {
  const res = await fetch(`${GATEWAY_URL}/me/export`, {
    credentials: "include",
  });
  if (!res.ok) {
    if (res.status === 401) handleUnauthenticated();
    // READ THE BODY. This endpoint is elevation-gated, so a 403 carries `step_up_required` — and
    // throwing a bare `http_403` would leave the UI unable to tell "verify a code" from any other
    // refusal, showing a status code to a user who can act on the real reason.
    const body = (await res.json().catch(() => null)) as {
      error?: string;
    } | null;
    throw new ApiError(res.status, body?.error ?? `http_${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "twoke-data-export.json";
  a.click();
  URL.revokeObjectURL(url);
}
/** Anonymize-and-retain account deletion. Requires a current step-up elevation window. */
export const gdprDelete = () =>
  req<{ deleted: boolean; mode: string }>("/me/gdpr-delete", {
    method: "POST",
    body: JSON.stringify({ confirm: true }),
  });

// ---- master/admin ----
export type MasterPerformance = {
  activeSubscribers: number;
  intents: {
    total: number;
    approved: number;
    rejected: number;
    expired: number;
    /** Proposals that became a real, filled trade of the master's (the card's percentage). */
    entered?: number;
  };
  /** The master's OWN trades that filled — never follower copies or unfilled orders. */
  positions: { total: number; open: number; closed: number; protected: number };
};

export const getAdminMasters = () =>
  req<{ masters: { id: string; name: string; style: string }[] }>(
    "/me/admin-masters",
  ).then((r) => r.masters);

// ---- platform_admin: dynamic pricing (item 7) ----
export const setPlanPrice = (plan: string, priceBaseUnits: string, product: "copy" | "spot" = "copy") =>
  req<{
    plan: string;
    priceBaseUnits: string;
    chainTxHash: string | null;
    chainStatus: string;
  }>(`/admin/plans/${plan}/price`, {
    method: "PUT",
    body: JSON.stringify({ priceBaseUnits, product }),
  });

// ---- platform_admin: audit log (item 8) ----
export type AuditEntry = {
  id: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  metadata: Record<string, unknown> | null;
  actor_email: string | null;
  created_at: string;
};
export const getAuditLog = (action?: string, limit = 100, offset = 0) =>
  req<{ entries: AuditEntry[]; total: number }>(
    `/admin/audit-log?limit=${limit}&offset=${offset}${action ? `&action=${encodeURIComponent(action)}` : ""}`,
  );

// ---- platform_admin: user mgmt (item 9), sub override (10), master CRUD (11) ----
export type AdminUser = {
  id: string;
  email: string;
  role: string;
  banned: boolean | null;
  ban_reason: string | null;
  anonymized_at: string | null;
  created_at: string;
};
export const getUsers = (q?: string, limit = 50, offset = 0) =>
  req<{ users: AdminUser[]; total: number }>(
    `/admin/users?limit=${limit}&offset=${offset}${q ? `&q=${encodeURIComponent(q)}` : ""}`,
  );
export const setUserRole = (id: string, role: string, confirm = false) =>
  req<{ userId: string; role: string }>(`/admin/users/${id}/role`, {
    method: "PUT",
    body: JSON.stringify({ role, confirm }),
  });
export const setUserBan = (id: string, banned: boolean, reason?: string) =>
  req<{ userId: string; banned: boolean }>(`/admin/users/${id}/ban`, {
    method: "PUT",
    body: JSON.stringify({ banned, reason }),
  });
/** Start impersonating a user (platform_admin + step-up + confirm). Swaps the session cookie. */
export const impersonateUser = (id: string) =>
  req<unknown>(`/admin/users/${id}/impersonate`, {
    method: "POST",
    body: JSON.stringify({ confirm: true }),
  });
/** Stop impersonating — restores the admin session. */
export const stopImpersonating = () =>
  req<unknown>("/admin/stop-impersonating", { method: "POST" });
/** `who` is a user uuid OR a sign-in email — the gateway resolves an email server-side, so the
 *  audit row always records the real user id. Support needs the email form because the console
 *  never shows a customer their uuid, so asking for one cannot be answered. */
/** One subscription, one account: a grant names the account it covers (`accountId`); `attach`
 *  ties the user's account-free row for that master to it instead (expiry unchanged). */
export const overrideSubscription = (
  who: string,
  masterId: string,
  plan: string,
  cancel = false,
  accountId?: string,
  attach = false,
) =>
  req<{ status: string; expiresAt?: string | null }>("/admin/subscriptions", {
    method: "POST",
    body: JSON.stringify({
      ...(who.includes("@") ? { email: who } : { userId: who }),
      masterId,
      plan,
      cancel,
      ...(accountId ? { accountId } : {}),
      ...(attach ? { attach: true } : {}),
    }),
  });
/** A user's accounts for the override panel (no key column): what each copies, and the
 *  account-free rows that cover every account until tied to one. */
export type AdminUserAccounts = {
  userId: string;
  accounts: { id: string; venue: string; label: string; isDemo: boolean; purpose: string;
    copies: { masterId: string; master: string; expiresAt: string } | null }[];
  accountFree: { id: string; masterId: string; master: string; plan: string; expiresAt: string }[];
};
export const getAdminUserAccounts = (who: string) =>
  req<AdminUserAccounts>(`/admin/subscriptions/accounts?who=${encodeURIComponent(who.trim())}`);
/** Grants without payment, PARKED until the user attaches each from the console: Spot (one year)
 *  and/or a copy of the master the admin picks (`copyMasterId`). A user who already holds or has
 *  parked it is skipped. `onlySubscribed` skips users with no active subscription; `dryRun`
 *  returns the plan only. */
export type GrantResult = {
  email: string;
  result?: "no_account" | "skipped_no_subscription";
  spot?: string | { status: string; expiresAt: string; reason?: string };
  copy?: string | { status: string; expiresAt: string; reason?: string };
  copyMaster?: string;
};
export const grantSubscriptions = (body: {
  emails: string[];
  spot: boolean;
  /** The master to grant; absent = Spot only. */
  copyMasterId?: string;
  copyPlan?: string;
  onlySubscribed?: boolean;
  dryRun?: boolean;
}) =>
  req<{ dryRun: boolean; count: number; results: GrantResult[] }>("/admin/grants", {
    method: "POST",
    body: JSON.stringify(body),
  });
export const createMaster = (name: string, style: string) =>
  req<{ id: string; name: string; style: string }>("/admin/masters", {
    method: "POST",
    body: JSON.stringify({ name, style }),
  });
export const updateMaster = (
  id: string,
  patch: { name?: string; style?: string },
) =>
  req<{ id: string; updated: boolean }>(`/admin/masters/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });

/** Upload a master lesson image + its §11.2 structured annotation (multipart, step-up gated). */
/** CORRECT a lesson already on record — the redraw path (2026-08-14).
 *
 *  The chart image is stored clean, so redrawing the same symbol at the same cutoff renders
 *  byte-identical bytes and `uploadTradeImage` returns `deduped: true` with the EXISTING lesson's
 *  id. That id is what this takes: the moment keeps one lesson, and only what is taught about it
 *  changes. The gateway keeps the previous annotation in `audit_log` before overwriting. */
export async function correctLessonAnnotation(
  imageId: string,
  annotation: unknown,
): Promise<{ imageId: string; lessonNo: number | null; corrected: boolean }> {
  const res = await fetch(
    `${GATEWAY_URL}/master/trade-images/${imageId}/annotation`,
    {
      method: "PUT",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ annotation }),
    },
  );
  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok) {
    if (res.status === 401) handleUnauthenticated();
    throw new ApiError(
      res.status,
      body.detail ?? body.error ?? `http_${res.status}`,
    );
  }
  return body;
}

export async function uploadTradeImage(
  masterId: string,
  file: File,
  annotation: unknown | null, // null = evidence image (grouped, non-entry): role only, no label
  group?: {
    tradeGroupId?: string;
    imageRole?: string;
    imageTimeframe?: string;
    /** migration 0021 — 'live' (traded) or 'retrospective' (drawn afterwards on historical
     *  klines). Omitted ⇒ the gateway defaults to 'live', which is right for the screenshot
     *  flow; only the chart annotator sends anything else. */
    provenance?: "live" | "retrospective" | "approved";
  },
): Promise<{
  imageId: string;
  skillNodeId?: string;
  contentHash: string;
  deduped: boolean;
  decision?: string;
  evidence?: boolean;
  imageRole?: string;
}> {
  const fd = new FormData();
  fd.append("master_id", masterId);
  fd.append("image", file);
  // Evidence images send NO annotation — only their role. The gateway stores a stub + skips
  // skill-linking; reconstruct treats them as evidence for the trade's entry-image label.
  if (annotation !== null) fd.append("annotation", JSON.stringify(annotation));
  // Optional multi-image grouping (migration 0008): several images → ONE trade.
  if (group?.tradeGroupId) fd.append("trade_group_id", group.tradeGroupId);
  if (group?.imageRole) fd.append("image_role", group.imageRole);
  if (group?.imageTimeframe) fd.append("image_timeframe", group.imageTimeframe);
  if (group?.provenance) fd.append("provenance", group.provenance);
  // NOTE: do NOT set content-type — the browser sets the multipart boundary itself.
  const res = await fetch(`${GATEWAY_URL}/master/trade-images`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok) {
    if (res.status === 401) handleUnauthenticated();
    throw new ApiError(
      res.status,
      body.detail ?? body.error ?? `http_${res.status}`,
    );
  }
  return body;
}

export type TradeImage = {
  id: string;
  imageUrl: string;
  decision: string | null;
  setup: string | null;
  symbol: string | null;
  timeframe: string | null;
  regime: string | null;
  uploaded_at: string;
  /** What the lesson actually TAUGHT. The stored image is a clean render — no geometry baked in,
   *  so the vision tower predicts it rather than reading it back — which means these counts are
   *  the only way to review a lesson without re-rendering the whole chart. */
  provenance?: "live" | "retrospective" | "approved";
  boxes?: number;
  channels?: number;
  confirmation?: string | null;
  has_breakout?: boolean;
  higher_timeframe?: string | null;
  /** WHEN THE LESSON WAS DECIDED — the only date the master thinks in. `uploaded_at` is when it
   *  was typed up, which for a retrospective lesson is a different day entirely, so a picker
   *  searched by that would not find the chart he is looking for. */
  lesson_no?: number | null;
  decided_at?: string | null;
  direction?: string | null;
};
/** One lesson's FULL annotation, for editing it. The list carries counts only. */
export const getLessonAnnotation = (imageId: string) =>
  req<{ imageId: string; lessonNo: number | null; annotation: unknown }>(
    `/master/trade-images/${imageId}`,
  );

export const getTradeImages = async (
  masterId: string,
  limit = 30,
  offset = 0,
  /** ONE LESSON BY NUMBER — filtered in SQL by the gateway, because `limit` is capped at 100 and
   *  a client-side filter over one page cannot find L37 in a corpus of 1000. */
  lessonNo?: number | null,
) => {
  const r = await req<{ images: TradeImage[]; total: number }>(
    `/master/${masterId}/trade-images?limit=${limit}&offset=${offset}` +
      (lessonNo ? `&lesson_no=${lessonNo}` : ""),
  );
  // `imageUrl` is a gateway PATH now, not a presigned MinIO URL. The gateway used to sign those
  // against the endpoint its own S3 client was built with — `http://minio:9000` in production —
  // which no browser can resolve, so every tile in the gallery rendered broken while the objects
  // were intact. Prefixing here keeps the one base URL this module already owns as the single
  // place that knows where the gateway lives.
  return {
    ...r,
    images: r.images.map((img) => ({
      ...img,
      imageUrl: img.imageUrl.startsWith("/")
        ? `${GATEWAY_URL}${img.imageUrl}`
        : img.imageUrl,
    })),
  };
};

export type DatasetStatus = {
  totalLessons: number;
  byDecision: { trade: number; wait: number; reject: number };
  distinctSetups: number;
  minReadyThreshold: number;
  ready: boolean;
};
export const getDatasetStatus = (masterId: string) =>
  req<DatasetStatus>(`/master/${masterId}/dataset-status`);

// ---- news gate (docs/MD/strategy/scalping.md; scalping master) ----
export type NewsEvent = {
  id: string;
  source: string;
  event: string;
  affected_assets: string[];
  scheduled_time_utc: string;
  impact: string;
  evidence_url: string | null;
};
export type NewsGate = {
  lastCheck: string | null;
  fresh: boolean;
  maxAgeMinutes: number;
};
export const getNews = (masterId: string) =>
  req<{ events: NewsEvent[]; gate: NewsGate }>(`/master/${masterId}/news`);
export const addNewsEvent = (
  masterId: string,
  body: {
    source: string;
    event: string;
    affectedAssets: string[];
    scheduledTimeUtc: string;
    impact: string;
    evidenceUrl?: string;
  },
) =>
  req<{ id: string }>(`/master/${masterId}/news`, {
    method: "POST",
    body: JSON.stringify(body),
  });
export const verifyNews = (masterId: string) =>
  req<NewsGate>(`/master/${masterId}/news/verify`, { method: "POST" });

/** Was an instant inside a RED-news blackout? Three states, and `unknown` is NOT `clear`:
 *  the calendar is hand-curated, so an empty stretch means we never looked, not that nothing
 *  happened. Advisory — the annotator warns, it never blocks a save. */
export type NewsWindow = {
  state: "blackout" | "clear" | "unknown";
  event?: string;
  at?: string;
  nearbyEvents?: number;
  blackoutMinutes?: number;
  reason?: string;
};
/** RED and ORANGE calendar events across a range, for painting on the chart.
 *  Uses the ENGINE's relevance rule (USD always, JPY only for a BOJ decision), not the older
 *  per-symbol token match that `getNewsWindow` still carries. */
export type NewsBand = {
  event: string;
  at: string;
  impact: "RED_HIGH" | "ORANGE";
  blackoutMinutes: number;
};
export const getNewsEvents = (masterId: string, from: string, to: string) =>
  req<{ events: NewsBand[] }>(
    `/master/${masterId}/news-events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  );

export const getNewsWindow = (masterId: string, at: string, symbol: string) =>
  req<NewsWindow>(
    `/master/${masterId}/news-window?at=${encodeURIComponent(at)}&symbol=${encodeURIComponent(symbol)}`,
  );

// ---- referrals (migration 0093, ReferralPay) ----
export type ReferralChannelKey = "whatsapp" | "telegram" | "discord" | "discord_user" | "tiktok" | "x";
export type MyReferral = {
  referrer: {
    status: "pending" | "approved" | "refused" | "stopped" | "expired";
    code: string | null;
    link: string | null;
    wallet: string;
    reason: string;
    audience: number;
    channels: Partial<Record<ReferralChannelKey, string>>;
    appliedAt: string;
    decidedAt: string | null;
  } | null;
  /** As a buyer: the referral code they joined with, and whether one can still be entered (only
   *  before their first paid subscription). */
  invited: { code: string | null; canEnter: boolean };
  /** This user may apply: they hold an active subscription of a product referrals are for. */
  canApply: boolean;
  /** The products referrals are for (the admin's ticks). */
  applyFor: { copy: boolean; spot: boolean };
  /** The commission in basis points (100 = 1 %). */
  bps: number;
  earnsOn: {
    copy: boolean;
    spot: boolean;
    allowed: { copy: boolean; spot: boolean };
    subscribed: { copy: boolean; spot: boolean };
  };
  referrals: {
    attributedAt: string;
    paidAt: string | null;
    product: string | null;
    chain: string | null;
    tx: string | null;
    token: string | null;
    priceUnits: string | null;
    commissionUnits: string | null;
  }[];
};
export const getMyReferral = () => req<MyReferral>("/me/referral");
export const applyReferral = (a: {
  wallet: string;
  reason: string;
  audience: number;
  channels: Partial<Record<ReferralChannelKey, string>>;
}) => req<{ ok: boolean; status: string }>("/me/referral/apply", { method: "POST", body: JSON.stringify(a) });
/** `auto`: carried from a link, attached only to a new account (the server checks). */
export const attachReferral = (code: string, auto = false) =>
  req<{ ok: boolean }>("/me/referral/attach", { method: "POST", body: JSON.stringify({ code, auto }) });
export type AdminReferrer = NonNullable<MyReferral["referrer"]> & {
  userId: string;
  email: string | null;
  name: string | null;
  bps: number | null;
  products: number | null;
  referred: number;
  paid: number;
  commissionUnits: string;
};
export const getAdminReferrers = () =>
  req<{ referrers: AdminReferrer[]; settings: { bps: number; products: number }; payable: string[] }>(
    "/admin/referrers",
  );
export const decideReferrer = (userId: string, action: "approve" | "refuse" | "stop", bps: number | null, products: number | null) =>
  req<{ referrer: unknown }>(
    `/admin/referrers/${encodeURIComponent(userId)}/decide`,
    { method: "POST", body: JSON.stringify({ action, bps, products }) },
  );
export const putReferralSettings = (bps: number, products: number) =>
  req<{ settings: { bps: number; products: number } }>(
    "/admin/referral-settings",
    { method: "PUT", body: JSON.stringify({ bps, products }) },
  );

export const getMasterPerformance = (masterId: string) =>
  req<{ performance: MasterPerformance }>(
    `/master/${masterId}/performance`,
  ).then((r) => r.performance);

// ---- platform_admin: dead-letter (poison message) visibility ----
/**
 * Undecodable venue frames the OMS parked instead of dropping. `oldestUnresolvedHours` is the
 * number to watch: current DLQ guidance alerts on AGE rather than count, because a count of 1
 * looks harmless forever — the first one in this system went unnoticed for five days.
 */
export type DeadLetter = {
  stream: string;
  streamSequence: string;
  subject: string;
  error: string;
  consumer: string;
  firstSeenAt: string;
  payloadPreview: string;
};
/** One copied (account, master) pair in the platform-admin limits table: who and what, plus the
 *  ENGINE's own daily-limits view for it — or the error that view returned (a dead key is a row). */
export type AdminLimitsRow = {
  accountId: string;
  label: string;
  venue: string;
  isDemo: boolean;
  authFailedAt: string | null;
  userId: string;
  email: string | null;
  masterId: string;
  masterName: string;
  expiresAt: string;
  view: (DailyLimitsView & { compound?: boolean }) | null;
  error: string | null;
};
export const getAdminDailyLimits = () =>
  req<{ accounts: AdminLimitsRow[]; readAt: string }>("/admin/daily-limits");

export const getDeadLetters = () =>
  req<{
    summary: { unresolved: number; oldestUnresolvedHours: number | null };
    deadLetters: DeadLetter[];
  }>("/admin/dead-letters");
export const resolveDeadLetter = (
  stream: string,
  streamSequence: string,
  note: string,
) =>
  req<{ resolved: boolean }>("/admin/dead-letters/resolve", {
    method: "POST",
    body: JSON.stringify({ stream, streamSequence, note }),
  });

// ---- paid-but-not-copying subscriptions (migration 0020) ----
/**
 * A subscription the user PAID for that is not copying yet, because the account named on-chain
 * already follows another master. Kept separate from `Subscription` on purpose: that type means
 * "copying now", this one means "paid, waiting on an account". Merging them would make the
 * dashboard claim coverage the allocator will not deliver.
 *
 * `expiresAt` matters here more than elsewhere — the clock runs from PURCHASE, so the days are
 * spending whether or not an account is attached. The user has to see that, not discover it.
 */
export type PendingSubscription = {
  id: string;
  masterId: string | null;
  masterName: string;
  product: "copy" | "spot";
  plan: string;
  expiresAt: string;
  accountId: string | null;
};
export const getPendingSubscriptions = () =>
  req<{ pending: PendingSubscription[] }>("/me/subscriptions/pending").then(
    (r) => r.pending,
  );
/** Attach a paid-but-parked subscription to one of the user's accounts, activating it. */
export const attachSubscription = (subscriptionId: string, accountId: string) =>
  req<{ attached: boolean; master: string }>(
    `/me/subscriptions/${subscriptionId}/attach`,
    { method: "POST", body: JSON.stringify({ accountId }) },
  );

/** Move an ACTIVE subscription to another of the user's accounts. Once every 14 days; the old
 *  account must hold nothing open; expiry and price do not change. */
export const moveSubscription = (subscriptionId: string, accountId: string) =>
  req<{ moved: boolean; master: string; movableAgainAt: string }>(
    `/me/subscriptions/${subscriptionId}/move`,
    { method: "POST", body: JSON.stringify({ accountId }) },
  );

/** THE VENUE'S OWN CAP on one kline request; the gateway clamps there too. */
export const KLINES_PER_REQUEST = 1000;

/**
 * MORE HISTORY THAN ONE REQUEST HOLDS (owner 2026-09-30: "can we have more candles in scalping on
 * annotation chart" — 6 days of 3m is 2,880 bars against a cap of 1,000).
 *
 * Walks BACKWARDS: the newest page first, then pages ending just before the oldest bar in hand,
 * until `bars` are collected or the source has nothing older. Returned oldest-first and strictly
 * increasing — a page is filtered to bars OLDER than what is already held, so an `end` the source
 * treats as inclusive cannot duplicate a bar, and a page that adds nothing ends the walk instead
 * of looping. `fetchPage` is a parameter so the walk is provable against the venue directly.
 *
 * With `bars` at or under the cap this is ONE request with `limit = bars`, exactly what the
 * caller sent before.
 */
export async function pageKlinesBack(
  fetchPage: (limit: number, end?: number) => Promise<Candle[]>,
  bars: number,
  end?: number,
): Promise<Candle[]> {
  let all = await fetchPage(Math.min(KLINES_PER_REQUEST, bars), end);
  // A hard stop on the number of requests, whatever the source answers.
  for (let i = 0; i < 12 && all.length > 0 && all.length < bars; i++) {
    const oldest = all[0]![0];
    const page = await fetchPage(
      Math.min(KLINES_PER_REQUEST, bars - all.length),
      oldest - 1,
    );
    const older = page.filter((c) => c[0] < oldest);
    if (older.length === 0) break;
    all = [...older, ...all];
  }
  return all;
}

/** `getKlines` reaching `bars` back — see `pageKlinesBack`. `opts.limit` is ignored. */
/** The engine's name for the last confirmation among a pane's COMPLETE bars, the swing engine's
 *  way (the annotator's box beside the replay). `candles` as `/market/klines` returned them;
 *  `style` picks the engine's rule; `formingLast` says the last bar is still forming. */
export type ConfirmationRead = { opened_at: string; direction: "long" | "short"; name: string; last_bar: boolean };
export type LastConfirmation = { closed: ConfirmationRead | null; bars: number };
export const readLastConfirmation = (candles: Candle[], style: string, formingLast: boolean) =>
  req<LastConfirmation>("/annotate/last-confirmation", { method: "POST", body: JSON.stringify({ candles, style, formingLast }) });

export async function getKlinesBack(
  symbol: string,
  interval: string,
  opts: NonNullable<Parameters<typeof getKlines>[2]>,
  bars: number,
): Promise<{ candles: Candle[] }> {
  const candles = await pageKlinesBack(
    async (limit, end) =>
      (await getKlines(symbol, interval, { ...opts, limit, ...(end ? { end } : {}) }))
        .candles,
    bars,
    opts.end,
  );
  return { candles };
}

/** One OHLCV bar, oldest-first: [openTime(ms), open, high, low, close, volume].
 *  The gateway normalises Bybit's newest-first strings, so the browser never sorts its own axis. */
export type Candle = [number, number, number, number, number, number];

/** Candles for the chart annotator (docs/MD/strategy/CHART_ANNOTATION.md).
 *
 *  Goes through the gateway, never straight to the venue: the bars drawn on must be the same bars
 *  `ml/klines.py` reconstructs the episode from, and one server-side path makes that true by
 *  construction. master_admin/platform_admin only. */
/** What THIS master's engine would have decided at `at` — read-only, never emits.
 *
 *  The annotator calls it whenever the decision cutoff moves, so the master can see what the engine
 *  made of the same bars while he draws instead of only when correcting a proposal. */
/** The candle SERIES a chart reads. `yahoo_gold` = real gold (Yahoo GC=F) for XAUTUSDT —
 *  the owner's reading series for that contract ("when I choose XAUTUSDT the gold Yahoo
 *  shows"), mirrored from inference's market_source.DEFAULT_SERIES. One entry, on purpose. */
export type PriceSeries =
  | "last"
  | "mark"
  | "index"
  | "yahoo_gold"
  | "yahoo_oil"
  | "yahoo_silver"
  // A typed TradFi market (2026-10-15): its own Yahoo future or share, `yahoo:PL=F`, `yahoo:TSLA`.
  | `yahoo:${string}`
  // cTrader only: BID is what cTrader's own chart draws; ASK is built from ask ticks.
  | "bid"
  | "ask";
export const GOLD_READ_DEFAULT: Record<string, PriceSeries> = {
  XAUUSDT: "yahoo_gold",
  XAUTUSDT: "yahoo_gold", // retired as the traded contract 2026-09-09; kept so old lessons chart
  CLUSDT: "yahoo_oil", // oil (2026-09-18): read on Yahoo CL=F, traded on CLUSDT — same as gold
  XTIUSD: "yahoo_oil", // cTrader's WTI (2026-09-28: "keep the hybrid also for ctrader oil and gold")
  XAGUSDT: "yahoo_silver", // silver: read on Yahoo SI=F, traded on XAGUSDT (cTrader names it so too)
  // THE INDICES READ THEIR OWN INSTRUMENT AT RATIO 1 (2026-10-15, market_source.INDEX_SERIES):
  // the ETF perpetuals the ETF, the cTrader CFDs the index — live on Yahoo, not a late future.
  QQQUSDT: "yahoo:QQQ",
  SPYUSDT: "yahoo:SPY",
  "US TECH 100": "yahoo:^NDX",
  "US 500": "yahoo:^GSPC",
  US30: "yahoo:^DJI", // the Dow, traded only on cTrader's US 30
};
/** Each Yahoo-read series' plain name and the Yahoo ticker it is fetched from — one table, so the
 *  chart's labels and the lesson's `source.read` cannot disagree about which future was read. */
export const YAHOO_SERIES_INFO: Record<string, { name: string; ticker: string }> = {
  yahoo_gold: { name: "gold", ticker: "GC=F" },
  yahoo_oil: { name: "oil", ticker: "CL=F" },
  yahoo_silver: { name: "silver", ticker: "SI=F" },
  "yahoo:SPY": { name: "S&P 500 (SPY)", ticker: "SPY" },
  "yahoo:QQQ": { name: "Nasdaq 100 (QQQ)", ticker: "QQQ" },
  "yahoo:^GSPC": { name: "S&P 500", ticker: "^GSPC" },
  "yahoo:^NDX": { name: "Nasdaq 100", ticker: "^NDX" },
  "yahoo:^DJI": { name: "Dow 30", ticker: "^DJI" },
};
/** Is this chart reading a Yahoo futures series (gold GC=F, oil CL=F) rather than the venue's? */
export const isYahooSeries = (p: string | null | undefined): boolean =>
  (!!p && /^yahoo:[A-Z0-9.^-]{1,12}(=F)?$/.test(p)) ||
  p === "yahoo_gold" ||
  p === "yahoo_oil" ||
  p === "yahoo_silver";

/** DISPLAY LABEL for a venue symbol (owner 2026-08-26: "replace PAXGUSDT by GOLD" on the
 *  frontend). `XAUTUSDT` is the contract we trade and is meaningless to a follower; the metal is
 *  the thing they chose. STRICTLY COSMETIC — every request, every stored value and every
 *  comparison still carries the real symbol. Anywhere a symbol is SENT rather than shown, this
 *  must NOT be used: a label in a payload would silently address a contract that does not exist. */
export const SYMBOL_LABEL: Record<string, string> = {
  XAUUSDT: "GOLD",
  XAUTUSDT: "GOLD", // same metal, retired contract — a follower should never see the difference
  CLUSDT: "OIL", // owner 2026-09-18: "CALL IT oil" — like GOLD
  XTIUSD: "OIL", // cTrader's WTI — same label as CLUSDT
  XAGUSDT: "SILVER",
  QQQUSDT: "NASDAQ 100",
  SPYUSDT: "S&P 500",
  "US TECH 100": "NASDAQ 100",
  "US 500": "S&P 500",
  US30: "DOW 30",
};
export const symbolLabel = (s: string | null | undefined): string =>
  SYMBOL_LABEL[(s ?? "").toUpperCase()] ?? s ?? "";
export const defaultSeriesFor = (symbol: string): PriceSeries =>
  GOLD_READ_DEFAULT[symbol.toUpperCase()] ?? TYPED_SERIES.get(symbol.toUpperCase()) ?? "last";
/** THE TYPED TRADFI MARKETS' SERIES, as the gateway resolved them (`/me/symbol-search` `series`):
 *  a symbol typed in the annotator reads its own Yahoo future or share by the same rule as the
 *  named markets above. Filled when the venue confirms the symbol; this page's memory only. */
const TYPED_SERIES = new Map<string, PriceSeries>();
export const rememberSeries = (symbol: string, series: string | null | undefined) => {
  if (series && isYahooSeries(series)) TYPED_SERIES.set(symbol.toUpperCase(), series as PriceSeries);
};
/** A Yahoo series' plain name and ticker — a named market's, or a typed one's own ticker. */
export const yahooInfo = (p: string): { name: string; ticker: string } | undefined => {
  if (YAHOO_SERIES_INFO[p]) return YAHOO_SERIES_INFO[p];
  const t = /^yahoo:(.+)$/.exec(p)?.[1];
  // A future by its market's name (platinum); a share by its ticker (TSLA share).
  return t ? { name: YAHOO_FUTURE_NAME[t] ?? (t.endsWith("=F") ? t : `${t} share`), ticker: t } : undefined;
};
const YAHOO_FUTURE_NAME: Record<string, string> = {
  "PL=F": "platinum", "PA=F": "palladium", "HG=F": "copper", "BZ=F": "Brent", "NG=F": "natural gas",
};

/** Intraday's or swing's Fibonacci SR, one reading per pane its Fibonacci is drawn on
 *  (`intraday_fib_sr`, `swing_fib_sr`). */
export type FibSrByPane = {
  kind: "fib_sr_by_pane";
  panes: { "1h": FibSr | null; "4h": FibSr | null; "1d"?: FibSr | null };
  params_version: string;
};

/** One Fibonacci SR reading (`rules/fib_sr.py`): A the swing's extreme (level 1), B its origin
 *  (level 0). A line is STRONG when the 15m chain turned on it from both sides. */
export type FibSr = {
  a: { p: number; t: number; anchor: string };
  b: { p: number; t: number; anchor: string };
  swing: "up" | "down";
  /** The deepest pullback since A, as a share of the swing. */
  pullback: number;
  tolerance: number;
  levels: {
    ratio: number;
    price: number;
    /** Swing lows on the line (it held as support). */
    from_above: number;
    /** Swing highs on the line (it held as resistance). */
    from_below: number;
    /** The 15m candles after A that reached the line and closed back above / below it. */
    rejected_from_above: number[];
    rejected_from_below: number[];
    strong: boolean;
    strong_by: "both_sides" | "rejections" | null;
  }[];
  params_version: string;
};

export async function getDecideAt(
  masterId: string,
  symbol: string,
  at: string,
  priceType?: PriceSeries,
  /** A recorded decision's id: the engine replays it from the row's OWN stored inputs (migration
   *  0064) and returns `guard` — identical to the row, or the exact fields that differ. */
  decisionId?: string,
  /** The venue on the chart (2026-09-28): the engine then reads THAT venue's bars — binance's
   *  public klines, or this master's cTrader account — instead of bybit's. Yahoo reads ignore it. */
  venue?: "bybit" | "binance" | "ctrader" | "mexc",
  /** The chart's execution pane (2026-10-19): "1", "2" or "5" asks that pane's stock engine (a
   *  single stock only — any other market is answered on the 3m); absent = the 3m. */
  exec?: "1" | "2" | "5",
): Promise<{
  action?: string;
  style?: string;
  /** "stored_inputs" | "persisted" | "walk" | null — what the channel lock stands on. */
  pin_source?: string | null;
  decision_id?: string | null;
  guard?: {
    identical: boolean;
    checked: string[];
    diffs: Record<string, { stored: string; replay: string }>;
  } | null;
  rationale?: string;
  reason?: string;
  error?: string;
  direction?: string;
  entry_price?: string;
  stop_loss?: string;
  take_profit?: string;
  /** TRUE when entry/stop/take_profit above are the TRADED CONTRACT's own prices, read off its
   *  candles instead of converted from the read series (owner 2026-09-03: "we won't use ratio
   *  anymore ... only XAUTUSDT"). `contract` is then absent — applying the basis to levels that
   *  are already the contract's is what displayed 4373.10 for a 4427.3 entry. */
  levels_on_contract?: boolean;
  /** A+SETUP (owner 2026-09-29): the 15m pivot structure behind this trade, e.g.
   *  "A+SETUP(LL … → HH … → new HL …)" — null when the trade is not one. Shown on the chart. */
  a_plus_setup?: string | null;
  /** The READ SERIES' levels, for DRAWING ONLY. The chart's candles are gold while the traded
   *  levels are XAUTUSDT's, ~1.2% apart (55.8 points on the 05:15Z short), so overlay lines drawn
   *  from the traded numbers float off the very candles they annotate. */
  draw_entry?: string;
  draw_stop_loss?: string;
  draw_take_profit?: string;
  /** When this setup's 6H confirmation closed, and how long before `at` that was. The route
   *  runs the pure rules and NOT the backend, so it carries none of the live suppressions and
   *  restates ONE trade at every cutoff until a new confirmation appears. Without these the
   *  panel showed the same long at Jun 29 8PM and Jun 30 midnight as if it were two setups. */
  setup_established?: string;
  setup_age_hours?: number;
  /** The engine's own drawing (DecisionGeometry): channels, boxes, breakout, confirmation, zone. */
  geometry?: Record<string, unknown>;
  /** THE FIBONACCI SR at this instant (`rules/fib_sr.py`): the swing's lines at a pullback of
   *  half the swing or more, with their touches; recorded only. Scalping reads the 15m; intraday
   *  its 1H and 4H, swing its 1H, 4H and 1D (`FibSrByPane`). */
  fib_sr?: FibSr | FibSrByPane | null;
  /** DEEP-ENTRY DAY reason (weekend / RED-news ET day) or null — rules/deep_entry.py. */
  volatile_day?: string | null;
  /** Is the deep entry OPEN at this instant (scalping's red-news window opens 2 h before the
   *  release, owner 2026-09-30). Absent from an older engine. */
  deep_open?: boolean;
  /** FOMC DAY (owner 2026-09-16) — set when `at` falls on an FOMC red ET day before 8 PM ET,
   *  whatever the engine then decided. The calendar's answer, not the decision's. */
  fomc_day?: string | null;
  /** THE ENGINE ACTUALLY REFUSED THIS SETUP FOR THE FOMC DAY (`AbstainDecision.fomc_day`), which
   *  is a stronger statement than `fomc_day` above: a complete setup existed and was recorded,
   *  and only the order was declined. Present only on that refusal. */
  fomc_abstain?: string;
  /** THE TRADE THE ENGINE WOULD HAVE TAKEN, carried on an FOMC refusal (owner: "show the trade
   *  engine info we will take but show ABSTAIN FOMC DAY"). Stamped after the contract mapping,
   *  so on a gold-read symbol these are the TRADED CONTRACT's prices, like the fields above.
   *  Named `intended_` end to end so a refused setup can never be read as a trade. */
  intended_direction?: string;
  intended_entry_price?: string;
  intended_stop_loss?: string;
  intended_take_profit?: string;
  intended_rr?: string;
  intended_levels_on_contract?: boolean;
  /** INDICATIVE contract levels for a gold-read decision (paxg_indicative.py): the gold levels
   *  converted onto XAUTUSDT at the DECISION instant's basis. The OMS re-measures at placement —
   *  its number is what trades; this one exists so the panel is not read with a mental ratio. */
  contract?: {
    entry: string;
    stop_loss: string;
    take_profit: string;
    ratio: string;
    paired_minute_utc: string;
    basis: string;
  };
  /** What already happened to this setup, walked forward from the candles — read-only, no
   *  position state. An Aug 19 short was shown as enterable 13h after it had filled AND been
   *  stopped out; "restated" alone could not say that, because a restatement of a live setup
   *  and of a finished one look identical. */
  setup_life?: {
    filled_at?: string;
    closed_at?: string;
    outcome?: "open" | "stopped" | "target";
    /** True when the live loop did NOT take this setup: the fill/stop is what WOULD have
     *  happened on the candles, not a trade (2026-10-01). */
    hypothetical?: boolean;
  };
  /** WOULD THE LIVE LOOP HAVE TRADED THIS? The panel runs the PURE rules; the backends add
   *  one-trade-per-zone and a 12-bar cooldown on top, so a complete setup here can still be a
   *  setup the loop refuses. Present for scalping and swing (both carry a zone rule); absent for
   *  intraday, which has none and chains state INTO decide instead, and absent whenever the
   *  engine abstained — there is nothing to suppress. */
  live_path?: {
    would_trade: boolean;
    suppressed_by:
      | "zone"
      | "cooldown"
      | "news"
      | "position"
      | "confirmation_taken"
      | "live"
      | null;
    reason: string;
    zone_held?: string | null;
    last_trade_at?: string;
    /** "live_record" = read from what the live loop actually decided about THIS setup
     *  (`rule_decisions`); absent = the replay of the tape (an instant the loop never ran). */
    source?: "live_record";
    taken_at?: string;
    intent_id?: string | null;
    refused_at?: string;
    first_seen_at?: string;
  };
}> {
  const q = new URLSearchParams({ masterId, symbol, at });
  if (decisionId) q.set("decisionId", decisionId);
  if (venue && venue !== "bybit") q.set("venue", venue);
  if (exec) q.set("exec", exec);
  return req(
    `/master/decide-at?${q}${priceType ? `&priceType=${priceType}` : ""}`,
  );
}

/** One stop move the live management would have made (inference `rules/manage_replay.py`). */
export type ManageMove = {
  /** The bar CLOSE at which it was proposed, epoch ms. */
  at: number;
  /** "target": scalping's L176 rule moved the TAKE-PROFIT to `proposed`; the stop is unchanged. */
  kind: "trail" | "breakeven" | "target";
  proposed: string;
  reason: string;
  /** false = the OMS ratchet would have held it (not better than the stop in place). */
  applied: boolean;
  stop_after: string;
  locked_r: string;
};
export type ManageAt = {
  symbol: string;
  pane: string;
  series: string;
  direction: "long" | "short";
  entry: string;
  initial_stop: string;
  opened_at: string;
  at: string;
  bars: number;
  tick: string;
  entry_type: "limit" | "market";
  /** The bar a limit entry filled in, epoch ms — null when it never filled by the cutoff. */
  filled_at: number | null;
  moves: ManageMove[];
  stopped_at: number | null;
  stopped_price: string | null;
  final_stop: string;
  /** Scalping's L176 rule only: where the target ended, and when it traded (epoch ms). */
  final_target?: string | null;
  target_hit_at?: number | null;
  rule?: string;
  rule_inputs?: {
    leg: [string, string] | null;
    fib_1618: string | null;
    double_top_tol_atr: string | null;
    first_top: string | null;
  } | null;
  note: string;
};
/** One trade of a trade sequence (migration 0081): a lesson, and where its stop/target ended up. */
export type SequenceTrade = {
  image_id: string;
  lesson_no: number | null;
  final_stop: number | null;
  final_target: number | null;
  exit_price: number | null;
  exit_time: string | null;
  exit_reason: "tp" | "sl" | "breakeven" | "manual" | null;
};
export type LessonSequence = {
  id: string;
  symbol: string;
  trades: SequenceTrade[];
  why_valid: string[];
  created_at: string;
  updated_at: string;
};
export const getLessonSequences = (masterId: string) =>
  req<{ sequences: LessonSequence[] }>(`/master/${masterId}/lesson-sequences`);
/** Needs the 2FA window, like saving a lesson. */
export const saveLessonSequence = (
  masterId: string,
  body: { trades: SequenceTrade[]; why_valid: string[] },
  id?: string,
) =>
  req<{ id: string; symbol: string; trades: SequenceTrade[] }>(
    id ? `/master/${masterId}/lesson-sequences/${id}` : `/master/${masterId}/lesson-sequences`,
    { method: id ? "PUT" : "POST", body: JSON.stringify(body) },
  );
export const deleteLessonSequence = (masterId: string, id: string) =>
  req<{ id: string; deleted: boolean }>(`/master/${masterId}/lesson-sequences/${id}`, {
    method: "DELETE",
  });

/** THE breakEVEN LESSON'S ENGINE ANSWER: every stop move from the linked trade's entry to `at`. */
export const getManageAt = (o: {
  masterId: string;
  symbol: string;
  at: string;
  openedAt: string;
  entry: number;
  stop: number;
  direction: "long" | "short";
  priceType?: string;
  entryType?: "limit" | "market";
  target?: number;
}) => {
  const q = new URLSearchParams({
    masterId: o.masterId,
    symbol: o.symbol,
    at: o.at,
    openedAt: o.openedAt,
    entry: String(o.entry),
    stop: String(o.stop),
    direction: o.direction,
  });
  if (o.priceType) q.set("priceType", o.priceType);
  if (o.entryType) q.set("entryType", o.entryType);
  if (o.target != null) q.set("target", String(o.target));
  return req<ManageAt>(`/master/manage-at?${q}`);
};

export async function getKlines(
  symbol: string,
  interval: string,
  opts?: {
    limit?: number;
    start?: number;
    /** Fetch the window ENDING here — how a retrospective lesson reaches old bars. */
    end?: number;
    /** Which venue's prices. Must match the annotation's `source`, since reconstruction fetches
     *  from the same market the drawing was made against. */
    venue?: "bybit" | "binance" | "ctrader" | "mexc";
    /** WHICH price on that venue. Bybit publishes the same contract as last-traded, mark and
     *  index, and its own chart toggles between them — so the contract alone does not pin down
     *  the candles. Recorded on the lesson for the same reason `price_scale` is. bybit only. */
    priceType?: PriceSeries;
    /** cTrader only: the master whose own account the bars are read through. */
    master?: string;
  },
): Promise<{
  symbol: string;
  interval: string;
  source: {
    venue: string;
    category: string;
    priceType?: string;
    read?: string;
  };
  candles: Candle[];
}> {
  const q = new URLSearchParams({ symbol, interval });
  if (opts?.limit) q.set("limit", String(opts.limit));
  if (opts?.start) q.set("start", String(opts.start));
  if (opts?.end) q.set("end", String(opts.end));
  if (opts?.venue) q.set("venue", opts.venue);
  if (opts?.priceType) q.set("priceType", opts.priceType);
  // cTrader bars are read through THIS master's own account (each broker quotes its own).
  if (opts?.master) q.set("master", opts.master);
  return req(`/market/klines?${q}`);
}

/** One market the platform trades and the masters that carry it (`GET /public/symbols`). */
export type PublicSymbol = { symbol: string; masters: string[] };
export async function getPublicSymbols(): Promise<{ symbols: PublicSymbol[] }> {
  return req(`/public/symbols`);
}

/** A model proposal awaiting the master's judgement (docs/MD/ml/AUTONOMOUS_BRAIN.md).
 *  `geometry` is what the ENGINE drew, in the annotator's own schema — so the chart renders a
 *  proposal exactly as it renders a human lesson. */
export type ProposedDecision = {
  id: string;
  symbol: string;
  decided_at: string;
  direction: string | null;
  entry_price: string | null;
  stop_loss: string | null;
  take_profit: string | null;
  rr: string | null;
  confidence: number;
  setup: string;
  rationale: string;
  geometry: unknown;
  outcome: string | null;
};

/** `total` is every proposal still waiting, not just this page — a batch replay can leave dozens
 *  behind the page limit, and at 20 rows a full queue looks exactly like a nearly empty one. */
export const getUnreviewedDecisions = (masterId: string, limit = 50) =>
  req<{ decisions: ProposedDecision[]; total?: number }>(
    `/master/${masterId}/decisions/unreviewed?limit=${limit}`,
  );

/** Approve or reject a proposal. REJECT IS NOT A LESSON — it stays a negative example and never
 *  becomes a trade_images row, so bad proposals cannot inflate the taught corpus. */
export const reviewDecision = (
  masterId: string,
  decisionId: string,
  verdict: "approve" | "reject",
  correction?: unknown,
) =>
  req<{ ok: true; verdict: string }>(
    `/master/${masterId}/decisions/${decisionId}/review`,
    { method: "POST", body: JSON.stringify({ verdict, correction }) },
  );

// ---- Autonomy: which masters are running, and in what mode (#60) ----
/** What the OPERATOR asked for, read from masters.autonomy_mode — not a claim that a process is
 *  currently honouring it. A master left on `live` with no inference service running is a real
 *  state, and one an operator needs to be able to see. */
export type MasterAutonomy = {
  id: string;
  name: string;
  style: string;
  autonomyMode: "off" | "shadow" | "live";
  autonomyUpdatedAt: string | null;
  /** The pairs this master trades (migration 0034); the engine re-reads it every tick. */
  symbols: string[] | null;
  /** Which setups this master PLACES (migration 0083); the rest are held as shadow trades. */
  placementSetups: PlacementSetups;
  placementUpdatedAt: string | null;
};
export type PlacementSetups = "a_plus" | "a_plus_interconnected" | "all";

export const getMasterAutonomy = () =>
  req<{ masters: MasterAutonomy[] }>("/admin/masters/autonomy").then(
    (r) => r.masters,
  );

/** The pairs a master trades (platform_admin). Followers may only accept a subset of these. */
export const setMasterSymbols = (masterId: string, symbols: string[]) =>
  req<{ masterId: string; symbols: string[] }>(
    `/admin/masters/${masterId}/symbols`,
    {
      method: "PUT",
      body: JSON.stringify({ symbols }),
    },
  );

/** The pairs YOU accept copies on for one subscription; `null` = none chosen, no trade copied. */
export const setSubscriptionPairs = (
  masterId: string,
  accountId: string | null,
  symbols: string[] | null,
) =>
  req<{ masterId: string; accountId: string | null; symbols: string[] | null }>(
    `/me/subscriptions/${masterId}/pairs`,
    { method: "PUT", body: JSON.stringify({ accountId, symbols }) },
  );

/** The scalping pane YOU copy on one subscription (migration 0097): only that pane's trades.
 *  "3" = no choice (the master's own trades), "3c" = the 3m clicked. */
export const setSubscriptionPane = (
  masterId: string,
  accountId: string,
  pane: "1" | "2" | "3" | "3c" | "5" | "dca",
) =>
  req<{ masterId: string; accountId: string; pane: string }>(
    `/me/subscriptions/${masterId}/pane`,
    { method: "PUT", body: JSON.stringify({ accountId, pane }) },
  );

/** Which setups a master places (platform_admin). Anything beyond A+ requires a step-up window;
 *  going back to A+ only does not. */
export const setMasterPlacement = (masterId: string, setups: PlacementSetups) =>
  req<{ masterId: string; setups: string; previous: string }>(
    `/admin/masters/${masterId}/placement`,
    { method: "PUT", body: JSON.stringify({ setups }) },
  );

/** Moving a master to `live` requires a step-up window; off/shadow do not. */
export const setMasterAutonomy = (
  masterId: string,
  mode: "off" | "shadow" | "live",
) =>
  req<{ masterId: string; mode: string; previous: string }>(
    `/admin/masters/${masterId}/autonomy`,
    { method: "PUT", body: JSON.stringify({ mode }) },
  );

// ---- ML training and the ML shadow (platform_admin, /masters) ----
export type MlRun = {
  id: string;
  requested_by: string;
  status: "running" | "done" | "failed";
  episodes: number | null;
  metrics: {
    episodes?: number;
    labels?: Record<string, number>;
    train_accuracy?: number;
    study?: string;
    quality?: string;
  } | null;
  error: string | null;
  shadow: boolean;
  promoted_at: string | null;
  created_at: string | null;
  finished_at: string | null;
};
export type MlShadowDecision = {
  symbol: string;
  decided_at: string | null;
  action: string;
  direction: string | null;
  confidence: number | null;
  rules_action: string | null;
  rules_direction: string | null;
};
export type MlMaster = {
  id: string;
  name: string;
  style: string;
  lessons: number;
  episodes: number;
  gate: number;
  shadow_run: string | null;
  runs: MlRun[];
  shadow_decisions: MlShadowDecision[];
};
export const getMlStatus = () =>
  req<{ available: boolean; masters: MlMaster[] }>("/admin/ml");
/** Needs the 2FA step-up window. Refused below 50 episodes by the trainer itself. */
export const startMlTraining = (masterId: string) =>
  req<{ run_id: string; status: string }>(`/admin/ml/${masterId}/train`, {
    method: "POST",
  });
/** The 2026 day-by-day replay: what history can be replayed, what was, and the running job. */
export type MlReplayJob = {
  style: string;
  symbol: string;
  first: string;
  last: string;
  status: "running" | "done" | "failed";
  started_at: string;
  finished_at: string | null;
  detail: string | null;
};
export type MlReplayStatus = {
  available: Record<string, Record<string, string[]>>;
  styles: string[];
  news_from: string;
  max_days: number;
  /** Hybrid contracts: their Yahoo series and how far back its minutes (3m/15m) and hours go. */
  hybrid: Record<string, { series: string; minutes_from: string | null; hours_from: string | null }>;
  replayed: { style: string; venue: string; symbol: string; day: string; trades: number }[];
  job: MlReplayJob | null;
};
export const getMlReplay = () => req<MlReplayStatus>("/admin/ml/replay");
/** One setup of a replayed day, as the replay wrote it, with its result played on the candles. */
export type OwnRead = { opened_at: string; direction: "long" | "short"; shapes: string[]; with_trade: boolean; last_bar: boolean; pane: "3d" | "1h" | "4h" | "1d" };
export type MlReplayRow = {
  decided_at: string;
  direction: "long" | "short";
  entry: string;
  stop: string;
  take_profit: string;
  setup: string | null;
  a_plus: string | null;
  breakout: string | null;
  news: string;
  version: string;
  exec_minutes: number;
  levels_on_contract: boolean;
  read_series: string | null;
  taken?: boolean;
  verdict?: string | null;
  a_plus_panes?: Record<string, boolean>;
  /** The setup's name inside its day, for a comment. */
  key: string;
  /** A+SETUP, interconnected (15m confirmation between the 0.5 and 0.786 of its leg), or other. */
  kind?: "a_plus" | "interconnected" | "other";
  /** The owner's comment on this trade, if any. */
  comment?: { text: string; by: string; at: string } | null;
  /** The study's fixes played on this trade (filled trades only). */
  fixes?: { fix: string; outcome: string; r: number | null }[];
  /** The pair's own last confirmation on the chart's 3-day bars COMPLETE before the decision
   *  (the forming bar left out), read as the annotator's box reads one: the shapes library's
   *  name, the swing candle list, bearish only at a high, bullish only at a low; `last_bar` = it
   *  closed on the last complete bar. */
  own_3d?: OwnRead | null;
  /** The same read on the pane the style's confirmation sits on: the 1h for scalping, the 4h for
   *  intraday, the daily sessions for swing. */
  own_pane?: OwnRead | null;
  result: {
    outcome: "tp" | "sl" | "open" | "cancelled" | "unfilled";
    r: number | null;
    filled_at: string | null;
    exited_at: string | null;
    candles: "venue" | "yahoo";
    /** The levels walked, in the candles' units (a hybrid's Yahoo levels on Yahoo candles). */
    entry?: string;
    stop?: string;
    take_profit?: string;
  };
};
/** Needs the 2FA step-up window. An empty text removes the comment. */
export const saveReplayComment = (v: {
  style: string; venue: string; symbol: string; day: string; key: string; text: string;
}) => req<{ saved: boolean }>("/admin/ml/replay/comment", { method: "POST", body: JSON.stringify(v) });
export const getMlReplayDay = (v: { style: string; venue: string; symbol: string; day: string }) =>
  req<{ style: string; venue: string; symbol: string; day: string; rows: MlReplayRow[] }>(
    `/admin/ml/replay/day?${new URLSearchParams(v)}`,
  );
/** Needs the 2FA step-up window. Writes trade files for the ML study; places nothing. */
export const startMlReplay = (v: { style: string; symbol: string; from: string; to: string }) =>
  req<{ status: string }>("/admin/ml/replay", { method: "POST", body: JSON.stringify(v) });
/** Needs the 2FA step-up window. Records the model beside the engine; it never trades. */
export const promoteMlShadow = (masterId: string, runId: string) =>
  req<{ master_id: string; shadow_run: string }>(`/admin/ml/${masterId}/shadow`, {
    method: "POST",
    body: JSON.stringify({ runId }),
  });
export type MlStat = {
  n: number;
  r_per_trade: number | null;
  total_r: number;
  wins: number;
  losses: number;
  lead_only: boolean;
};
export type MlQuestion = {
  kind: string;
  question: string;
  answer: string;
  n: number;
  lead_only: boolean;
  answered: boolean;
};
export type MlPairSummary = {
  pair: string;
  group: string;
  run_at: string;
  trades: MlStat & { lessons: number; engine: number };
  questions: number;
  unanswered: number;
};
export type MlPairMemory = {
  latest: {
    pair: string;
    group: string;
    run_at: string;
    period: { from: string | null; to: string | null };
    trades: MlStat & { lessons: number; engine: number };
    exits: {
      take_profit: Record<string, MlStat & { vs_today: string }>;
      breakeven: Record<string, MlStat & { vs_today: string }>;
    };
    held_longer: {
      n: number;
      median_run_r: number | null;
      reached_3r: number;
      reached_5r: number;
      reached_10r: number;
    };
    by_session: Record<string, MlStat>;
    by_news_timing?: Record<string, MlStat>;
    by_setup: Record<string, MlStat>;
    questions: MlQuestion[];
  };
  history: { run_at: string; trades: MlStat }[];
};
export const getMlMemory = () => req<{ pairs: MlPairSummary[] }>("/admin/ml/memory");
export const getMlPairMemory = (pair: string) =>
  req<MlPairMemory>(`/admin/ml/memory/${encodeURIComponent(pair)}`);
export type MlVariant = {
  id: string;
  style: string;
  market_group: string;
  kind: "take_profit" | "breakeven";
  value_r: number;
  source: string | null;
  min_trades: number;
  status: "shadow" | "promoted" | "stopped";
  approved_at: string;
  trades: number;
  history: number;
  live: number;
  today_r: number | null;
  variant_r: number | null;
  better_in_both_halves: boolean;
  promotable: boolean;
};
export const getMlVariants = () =>
  req<{
    variants: MlVariant[];
    values: Record<string, number[]>;
    groups: string[];
  }>("/admin/ml/variants");
/** Needs the 2FA window. Approving starts a SHADOW measurement — it trades nothing. */
export const approveMlVariant = (v: {
  style: string;
  market_group: string;
  kind: string;
  value_r: number;
  min_trades: number;
  source?: string;
}) => req<{ id: string; status: string }>("/admin/ml/variants", { method: "POST", body: JSON.stringify(v) });
export const evaluateMlVariants = () =>
  req<{ ok: boolean }>("/admin/ml/variants/evaluate", { method: "POST" });
/** `promote` needs the 2FA window and is refused below the minimum or without both halves better. */
export const actOnMlVariant = (id: string, action: "stop" | "promote") =>
  req<{ id: string; status: string }>(`/admin/ml/variants/${id}/${action}`, { method: "POST" });
export const getMlReports = () =>
  req<{ reports: { name: string; bytes: number; modified: string }[] }>("/admin/ml/reports");
/** One rate of the study: trades, R per trade, how many positive, total R. */
export type StudyStat = { n: number; r_per_trade: number; positive: number; total_r: number } | null;
export type StudyGroup = {
  style: string;
  group: string;
  n: number;
  exits: {
    kind: "tp" | "breakeven";
    value_r: number;
    label: string;
    all: StudyStat;
    first_half: StudyStat;
    second_half: StudyStat;
    verdict: string;
  }[];
  breakeven_vs_never: { at_r: number; n: number; saved: number; cost: number; net_r: number }[];
  engine_vs_wait: {
    n: number;
    plan: StudyStat;
    engine: StudyStat;
    wait: StudyStat;
    too_soon: { source: string; ref: string; symbol: string; at: string }[];
    engine_better: number;
  } | null;
  held_longer: { n: number; median_r: number; reached: Record<string, number> } | null;
};
export type StudyData = {
  coverage: {
    plans: number;
    lessons: number;
    engine: number;
    replay: number;
    filled: number;
    stop_before_fill: number;
    open_or_unfilled: number;
    not_measurable: Record<string, number>;
  };
  groups: StudyGroup[];
  day_by_day: {
    pair_days: number;
    with_choice: number;
    first?: StudyStat;
    best_priced?: StudyStat;
    best?: StudyStat;
  };
  falsify: string[];
};
export type StudyTrade = {
  style: string;
  symbol: string;
  direction: string;
  at: string;
  source: string;
  ref: string;
  group: string;
};
/** One trade-memory policy's measured result (same shape as `trade_memory.policies`). */
export type StudyDoc_MemoryPolicy = {
  name: string;
  placed: number;
  filled: number;
  tp: number;
  sl: number;
  result: StudyStat;
  trait?: string;
  value?: string;
  /** Deflated Sharpe: chance the edge is real after the number of ideas tested. */
  dsr?: number | null;
  survives_search?: boolean;
};
/** One set of trades sized flat (1 R each) and with the compound ladder, in base-risk units. */
export type StudyCompound = { trades: number; flat_r: number; flat_max_drawdown_r: number;
  compound_r: number; compound_max_drawdown_r: number; steps: Record<string, number> };
export type StudyWhen = {
  trades: number;
  result: StudyStat;
  by_month: { value: string; result: StudyStat }[];
  by_weekday: { value: string; result: StudyStat }[];
  by_session: { value: string; result: StudyStat }[];
  by_hours: { value: string; result: StudyStat }[];
  by_pair: { value: string; result: StudyStat }[];
  pair_profiles: { pair: string; result: StudyStat; sessions: Record<string, StudyStat>;
    weekdays: Record<string, StudyStat>; best_fix: { fix: string; net_r: number } | null;
    compound?: StudyCompound }[];
};
/** Staying in longer: R by trade duration, and the trades held 24h / 48h with the stop kept. */
export type StudyHoldLonger = {
  by_duration: { value: string; result: StudyStat }[];
  held: Record<string, { trades: number; today_r: number; held_r: number; better: number; worse: number }>;
  per_pair: Record<string, Record<string, { trades: number; today_r: number; held_r: number; better: number; worse: number }>>;
};
/** One setup type's results on its own (A+, or interconnected channel). */
export type StudyTypeView = {
  setups: number;
  when: StudyWhen;
  policies: { name: string; result: StudyStat; dsr?: number | null; compound?: StudyCompound }[];
  best_trade: { trait: string; value: string; result: StudyStat; best_of_day: number }[];
  hold_longer: StudyHoldLonger;
};
/** A rate with its 95% interval (`exit_study._wilson_rate`); rate, low and high are 0..1. */
export type StudyRate = { n: number; rate: number | null; low: number | null; high: number | null };
export type StudySpDaily = {
  source: string;
  spy_days: { date: string; open?: number; close?: number; return_pct?: number;
    spy: "up" | "down" | "flat" }[];
  markets: {
    market: string;
    same_day: StudyRate;
    correlation: number | null;
    up_on_spy_up: StudyRate;
    down_on_spy_down: StudyRate;
    next_day: StudyRate;
  }[];
  /** SPY's 4H / daily confirmation candles and whether each pair follows them. */
  confirmations?: {
    signals: Record<string, number>;
    markets: string[];
    shapes: { pane: "4H" | "1D"; shape: string; direction: "long" | "short"; signals: number;
      follow: Record<string, StudyRate> }[];
    clashes: number;
    who_wins: Record<string, { "4h": { "4H"?: number; "1D"?: number };
      "24h": { "4H"?: number; "1D"?: number } }>;
  };
};
/** One hypothesis of the ledger with its critic checks (`exit_study.update_ledger`). */
export type StudyHypothesis = {
  id: string;
  statement: string;
  kind: string;
  verdict: "confirmed" | "open" | "refuted";
  /** The first test is the discovery; a later one with `fresh_days` > 0 ran on days it never saw. */
  history: { at: string; n: number; fresh_days?: number; r_per_trade?: number | null;
    reference_r_per_trade?: number | null; dsr?: number | null; beats_reference: boolean }[];
  checks: { check: string; passed: boolean; detail: string; repair: string }[];
  /** Replayed days it has been measured on (absent in a ledger written before 2026-10-07). */
  days_seen?: number;
};
/** The analyst's exit study as data (`twoke_ml.exit_study`, written beside its markdown). */
export type StudyDoc = {
  kind: "exit_study";
  generated_at: string;
  title: string;
  lessons: StudyData;
  sequences: {
    symbol: string;
    trades: {
      lesson_no: number | null;
      exit_reason: string | null;
      exit_price: number | null;
      real_r: number | null;
      drawn_r: number | null;
      measurable: boolean;
    }[];
    total_real_r: number;
    total_drawn_r: number;
    open_trade_breaks: string[];
    why: string[];
  }[];
  replay: { n: number; left_out_open_trade: number; study: StudyData } | null;
  trade_reviews: (StudyTrade & { result_r: number | null; findings: string[] })[];
  already_played: (StudyTrade & { why: string })[];
  open_trade_blocked: StudyTrade[];
  engine_decisions: { n: number; filled: number; at_3r: StudyStat; used_as_evidence: boolean };
  hypotheses?: StudyHypothesis[];
  /** SPY against every market's daily close (absent in a report written before 2026-10-07). */
  sp_daily?: StudySpDaily;
  /** The trade-memory policies replayed on every A+ setup (docs/MD/strategy/memorytrade.md). */
  trade_memory?: {
    style: string;
    /** When the engine's own trades made R, and each pair's profile (absent before 2026-10-07). */
    when?: StudyWhen;
    hold_longer?: StudyHoldLonger;
    /** Every setup the engine found, A+ or not, played alone (absent before 2026-10-07). */
    every_setup?: {
      setups: number;
      a_plus: number;
      by_a_plus: { value: string; result: StudyStat }[];
      when: StudyWhen;
      policies: { name: string; result: StudyStat; dsr?: number | null; compound?: StudyCompound }[];
      /** Each setup type alone — the owner's A+ / interconnected channel check boxes. */
      by_type?: Record<string, StudyTypeView>;
      hold_longer?: StudyHoldLonger;
      /** The best-trade model trained on every setup, the setup type one of its inputs. */
      why_model?: NonNullable<StudyDoc["trade_memory"]>["why_model"];
      /** Each trait value's result on every setup, and how often the day's best trade had it. */
      best_trade?: { trait: string; value: string; result: StudyStat; best_of_day: number }[];
      patterns: { pattern: string; result: StudyStat; days: number; days_positive: number;
        dsr?: number | null }[];
    };
    /** The owner's comments on replayed trades. */
    owner_comments?: { style: string; venue: string; symbol: string; day: string; trade: string;
      text: string; by: string; at: string }[];
    pairs: string[];
    days: number;
    setups: number;
    taken_today: number;
    policies: {
      name: string;
      today: boolean;
      expiry_min: number | null;
      day_rule: string | null;
      placed: number;
      filled: number;
      tp: number;
      sl: number;
      still_open: number;
      expired: number;
      played: number;
      unfilled: number;
      result: StudyStat;
      conflicts: number;
      skipped_busy: number;
      skipped_day_used: number;
      median_fill_delay_min: number | null;
      second_chances?: number;
      second_chance_result?: StudyStat;
      trait?: string;
      value?: string;
    }[];
    fill_delay: { from_min: number; to_min: number | null; result: StudyStat }[];
    one_a_day_by_trait?: StudyDoc_MemoryPolicy[];
    ideas_tested?: number;
    /** The best trade, and why: every trait together, held-out scored (`best_trade_model`). */
    why_model?: {
      verdict: string;
      trades?: number;
      test?: number;
      auc?: number;
      brier?: number;
      brier_base_rate?: number;
      beats_base_rate?: boolean;
      raises_win_chance?: { trait: string; weight: number }[];
      lowers_win_chance?: { trait: string; weight: number }[];
    };
    walk_forward?: {
      split_at: string;
      chosen: string;
      in_sample: StudyStat;
      out_of_sample: StudyStat;
      no_limit_out_of_sample: StudyStat;
      holds: boolean;
    } | null;
    big_move?: {
      big_candle_atr: number;
      move_before: { bucket: string; result: StudyStat }[];
      since_big_candle: { bucket: string; result: StudyStat }[];
      wait_policies: StudyDoc_MemoryPolicy[];
    };
    per_pair?: {
      pair: string;
      no_limit: StudyDoc_MemoryPolicy;
      one_a_day: StudyDoc_MemoryPolicy;
      best_pattern: string | null;
      best_pattern_result: StudyStat;
    }[];
    /** Every A+ trade (no day limit) ranked by pattern — by the share of days it made money. */
    patterns?: {
      pattern: string;
      parts: number;
      result: StudyStat;
      win_rate: number;
      days: number;
      days_positive: number;
      best_of_day: number;
    }[];
    best_trade?: { trait: string; value: string; result: StudyStat; best_of_day: number }[];
    best_trade_days?: number;
    sequence?: { case: string; result: StudyStat }[];
  } | null;
};
export const getMlReport = (name: string) =>
  req<{ name: string; markdown: string; data: StudyDoc | null }>(
    `/admin/ml/reports/${encodeURIComponent(name)}`,
  );
/** Stopping never waits for a code. */
export const stopMlShadow = (masterId: string) =>
  req<{ master_id: string; shadow_run: null }>(`/admin/ml/${masterId}/shadow`, {
    method: "DELETE",
  });

// ---- Announcements: the home-page popup card (platform_admin managed, migration 0035) ----
/** Where the popup sits on the home page (owner: "right, left, top or in the center"). */
export type AnnouncementPosition = "right" | "left" | "top" | "center" | "full";

export type Announcement = {
  id: string;
  title: string;
  body: string;
  ctaLabel: string | null;
  ctaUrl: string | null;
  countdownTo: string | null;
  visible: boolean;
  layout: "card" | "flyer";
  showTime: boolean;
  position: AnnouncementPosition;
  startsAt: string | null;
  endsAt: string | null;
  /** Resolved by the gateway: countdownTo, else a future startsAt, else endsAt. */
  countdownTarget: string | null;
  countdownSource: "countdown_to" | "starts_at" | "ends_at" | null;
  hasImage: boolean;
  imageType: string | null;
  createdAt: string;
  updatedAt: string;
};

/** PUBLIC: what the home page shows right now (server-filtered: visible + window). */
export const getActiveAnnouncements = () =>
  req<{ announcements: Announcement[] }>("/announcements/active").then(
    (r) => r.announcements,
  );

/** The image URL the browser loads — served only while the announcement is visible. */
export const announcementImageUrl = (a: Announcement) =>
  `${GATEWAY_URL}/announcements/${a.id}/image?v=${encodeURIComponent(a.updatedAt)}`;

export const getAnnouncements = () =>
  req<{ announcements: Announcement[] }>("/admin/announcements").then(
    (r) => r.announcements,
  );

export type AnnouncementInput = {
  title: string;
  body: string;
  ctaLabel: string | null;
  ctaUrl: string | null;
  countdownTo: string | null;
  visible: boolean;
  layout: "card" | "flyer";
  showTime: boolean;
  position: AnnouncementPosition;
  startsAt: string | null;
  endsAt: string | null;
};

export const createAnnouncement = (input: AnnouncementInput) =>
  req<{ announcement: Announcement }>("/admin/announcements", {
    method: "POST",
    body: JSON.stringify(input),
  }).then((r) => r.announcement);

export const updateAnnouncement = (id: string, input: AnnouncementInput) =>
  req<{ announcement: Announcement }>(`/admin/announcements/${id}`, {
    method: "PUT",
    body: JSON.stringify(input),
  }).then((r) => r.announcement);

/** THE switch: show or hide, whenever — a bare { visible } so the text is never re-sent. */
export const setAnnouncementVisible = (id: string, visible: boolean) =>
  req<{ id: string; visible: boolean }>(`/admin/announcements/${id}`, {
    method: "PUT",
    body: JSON.stringify({ visible }),
  });

export const deleteAnnouncement = (id: string) =>
  req<{ id: string; deleted: boolean }>(`/admin/announcements/${id}`, {
    method: "DELETE",
  });

export async function uploadAnnouncementImage(
  id: string,
  file: File,
): Promise<void> {
  const fd = new FormData();
  fd.append("image", file);
  // NOTE: do NOT set content-type — the browser sets the multipart boundary itself.
  const res = await fetch(`${GATEWAY_URL}/admin/announcements/${id}/image`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok)
    throw new ApiError(res.status, body.error ?? `http_${res.status}`, body);
}
